import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import useScheduledDate from "../../hooks/useScheduledDate";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
  GALLERY_SOURCE_SOCIAL_POSTS,
  GALLERY_SELECT_SOURCE,
  BUTTON_CREATE_FROM_SCRATCH,
  TEMPLATES_TITLE,
  FILTERS_GALLERY_RESET_FILTERS,
  GALLERY_FILTER_APPROVAL_STATUS,
  CAMPAIGN_APPROVAL_STATUS_APPROVED,
  CAMPAIGN_APPROVAL_STATUS_PENDING,
  CAMPAIGN_APPROVAL_STATUS_REJECTED,
  BUTTON_CREATE_CAMPAIGN2,
  GALLERY_CREATE_CAMPAIGN_LEAVE_FLOW_TITLE,
  GALLERY_CREATE_CAMPAIGN_LEAVE_FLOW_DESCRIPTION,
  INFORMATION,
  VIEW_CALENDAR,
  GALLERY_MAX_SELECTIONS_REACHED,
} from "../../i18n/keysTranslations";
import ModalMedia from "../Modal/ModalMedia";
import ModalGalleryAdsPlatforms from "./ModalGalleryAdsPlatforms";
import ModalGalleryAdAccount from "./ModalGalleryAdAccount";
import ModalSelectGoogleDriveAccount from "./ModalSelectGoogleDriveAccount";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import ModalBasicLayout from "./ModalBasicLayout";
import { setModalOpenState } from "../../actions/ui";
import Loader from "../Loaders/Loader";
import BackDropLoader from "../Loaders/BackDropLoader";
import Button from "../Buttons/Button";
import { NewFeatureTooltip } from "../../features/newFeatures";
import Gallery from "../Gallery/Gallery";
import {
  startGetGalleryContent,
  startAddContentsToList,
  startUpdateCarouselImageUrl,
  startBumpContentInList,
  startBumpAndUpdateContentInList,
  startResizeImage,
  startResizeImagesBatch,
  startAnimateImage,
  startGetDriveAccessTokenForPicker,
  startSaveGoogleDriveFilesInStorage,
  startSetLastSelectedIntegrationDataByPlatform,
} from "../../actions/user";
import { usePagination } from "../../hooks/usePagination";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  GALLERY_SOURCES,
  GOOGLE_DRIVE,
  ALL_FILTER,
  FILTERS_MEDIA_DATE_RANGE,
  contentTypeOptions,
  dimensionsOptions,
  ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
  MODE_BACK_DROP_CONNECT,
  GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD,
  ROUTE_CAMPAIGNS,
  ROUTE_GALLERY,
  LINKEDIN,
} from "../../utils/constants";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import UploadFileButton from "../Buttons/UploadFileButton";
import GalleryUploadMenu from "../Gallery/GalleryUploadMenu";
import { startSaveMediaInStorage } from "../../actions/user";
import { usePlatformConfigurations } from "../../hooks/usePlatformConfigurations";
import ModalCanva from "./ModalCanva";
import ModalCalendar from "./ModalCalendar/ModalCalendar";
import { getExtensionFile } from "../../utils/string";
import ModalGalleryCanva from "./ModalGalleryCanva";
import ImportDropdownButton from "./ModalGallery/ImportDropdownButton";
import ButtonCanvaAction from "../Buttons/ButtonCanvaAction";
import ButtonGoogleDriveAction from "../Buttons/ButtonGoogleDriveAction";
import FilterSelect from "../Form/FilterSelect";
import Switch from "../Form/Switch";
import { BORDER_GRAY_COLOR } from "../../utils/colors";
import { useMultipleMediaDimensions } from "../../hooks/useMediaDimensions";
import TitlePage from "../Texts/TitlePage";
import { BadgeCheck, FileText, LayoutTemplate, Maximize2 } from "lucide-react";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import IconButton from "../Buttons/IconButton";
import { useContentModals } from "../../contexts/contentModalsContext";
import { useTemplatesModals } from "../../contexts/templatesModalsContext";
import ModalImageEdit from "./ModalImageEdit";
import ModalBatchImageEdit from "./ModalImageEdit/ModalBatchImageEdit";
import ModalAnimateImagePreview from "./ModalAnimateImagePreview";
import { AutoAwesome as SparklesIcon } from "@mui/icons-material";
import { getIsBlockedActions } from "../../actions/getters";
import { useGalleryPermissions } from "../../hooks/useGalleryPermissions";
import { useEnsureGalleryUploadFolder } from "../../hooks/useEnsureGalleryUploadFolder";
import { getSelectedDirectoryName } from "../../utils/galleryDirectoryUpload";
import {
  loadGoogleScripts,
  openGoogleDrivePicker,
} from "../../services/googleDrivePicker";
import { useAnimateImageWithPreview } from "../../hooks/useAnimateImageWithPreview";
import { ConfirmAlert } from "../Alerts/Alerts";
import {
  DELETE_MULTIPLE_CONTENT_CONFIRMATION_TITLE,
  DELETE_MULTIPLE_CONTENT_CONFIRMATION_DESCRIPTION,
  BUTTON_DELETE_CONTENT,
  BUTTON_DOWNLOAD,
  SELECT_CONTENT_CONFIRMATION_TEXT,
  GALLERY_VIEW_SCORES,
  GALLERY_FOLDERS_MOVE,
  GALLERY_FOLDERS_ALL,
} from "../../i18n/keysTranslations";
import {
  startDeleteContentFromListAndStorage,
  startGetAwsDownloadUrl,
  startDownloadCarouselZip,
  startDownloadUrlsAsZip,
  startGetContentItemById,
  startGetContentItemsByIds,
} from "../../actions/user";
import { isCarouselItem, getCarouselUrls } from "../../utils/carouselUtils";
import { getUniqueID } from "../../utils/numbers";
import {
  getCenteredOffsetInContainer,
  smoothScrollContainerTo,
} from "../../utils/scrollContainer";
import {
  findGalleryItemBySelectionId,
  getGalleryItemSelectionId,
  getGalleryItemsBySelectionIds,
  getBatchEditEligibleItems,
  getBatchResizeEligibleItems,
  getCarouselCreationEligibleItems,
  countBatchResizeSourceImages,
  estimateBatchResizeCreditCount,
} from "../../utils/gallerySelection";
import MultiFormatResizeMenu from "../Gallery/MultiFormatResizeMenu";
import PhotoSizeSelectLargeIcon from "@mui/icons-material/PhotoSizeSelectLarge";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import GallerySelectedCountChip from "../Gallery/GallerySelectedCountChip";
import {
  applyGalleryReorderPreservingPlaceholders,
  bumpGalleryItemToFront,
  bumpGalleryItemsToFront,
  getGalleryBumpTimestamps,
} from "../../utils/galleryContentReorder";
import {
  formatGalleryContentEntriesFromResponse,
  groupGalleryCarousels,
} from "../../utils/galleryContentFormat";
import { mergePublishedIntoGalleryItems } from "../../utils/galleryPublishedSync";
import {
  buildResizeImagePayload,
  getResizingFormatItemPatch,
  clearResizingFormatItemPatch,
} from "../../utils/buildResizeImagePayload";

import { LaunchError } from "../../utils/errors";

const BATCH_RESIZE_PLACEHOLDER_PREFIX = "batch-resize-placeholder";

const getBatchResizePlaceholderId = (format) =>
  `${BATCH_RESIZE_PLACEHOLDER_PREFIX}-${format}`;

const isBatchResizePlaceholder = (item) =>
  typeof item?.id === "string" &&
  (item.id === BATCH_RESIZE_PLACEHOLDER_PREFIX ||
    item.id.startsWith(`${BATCH_RESIZE_PLACEHOLDER_PREFIX}-`));
import ContentApprovalReviewModal from "../ContentApproval/ContentApprovalReviewModal";
import ApprovalEnableSuccessDialog from "../ContentApproval/ApprovalEnableSuccessDialog";
import {
  buildContentApprovalDataFromItem,
  startBumpContentApprovalDraft,
  startEnableContentApproval,
  startListenerContentApproval,
  stopListenerContentApproval,
} from "../../actions/contentApproval";
import {
  getContentApprovalContentIDs,
  getContentApprovalStateForContent,
  isReviewableContentApprovalStatus,
  mergeGalleryItemWithApprovalData,
} from "../ContentApproval/contentApprovalUtils";
import { startReplaceContentMediaInList } from "../../actions/user";
import { SimpleAlert } from "../Alerts/Alerts";
import {
  CONTENT_APPROVAL_SAVE_REVISION_SUCCESS_DESCRIPTION,
  READY,
  CONTENT_APPROVAL_CONFIRM_ENABLE_TITLE,
  CONTENT_APPROVAL_CONFIRM_ENABLE_DESCRIPTION,
  CAMPAIGN_APPROVAL_SEND_FOR_REVIEW,
  BUTTON_SEND,
  BUTTON_CANCEL,
  MODAL_BATCH_IMAGE_EDIT_FOOTER_ACTION,
  GALLERY_CREATE_CAROUSEL,
} from "../../i18n/keysTranslations";
import {
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_WARNING,
} from "../../utils/constants";
import GalleryFolderBar from "../Gallery/GalleryFolderBar";
import ModalCreateContentFolder from "./ModalCreateContentFolder";
import ModalMoveContentsToFolder from "./ModalMoveContentsToFolder";
import ModalRenameContentFolder from "./ModalRenameContentFolder";
import { useGalleryFolderSession } from "../../hooks/useGalleryFolderSession";
import { useFolderAwareDeleteAction } from "../../hooks/useFolderAwareDeleteAction";
import { getCommonFolderID } from "../../utils/contentFolderUtils";
import useInitialPreviewSelection from "../../hooks/useInitialPreviewSelection";
import { buildInitialCreativesFromGalleryItems } from "../../utils/galleryInitialCreatives";
import { buildCreateCampaignListUrl } from "./ModalCreateAdsCampaign/createAdsCampaignWizardUtils";
import { HOME_GALLERY_EDIT_ACTION } from "../../utils/homeGalleryEditActions";

const paddings = {
  pl: { xs: 1.5, sm: 5 },
  pr: { xs: 2.5, sm: 6 },
};

const getGalleryToolbarRowSx = ({ backgroundColor = "common.white" } = {}) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  py: 2,
  pr: paddings.pr,
  pl: paddings.pl,
  backgroundColor,
  borderBottom: `1px solid ${BORDER_GRAY_COLOR}`,
});

/** Footer stacks into centered columns when viewport width is below 700px. */
const modalGalleryFooterStackMq = "@media (max-width: 699px)";
import { useNotifications } from "../../hooks/useNotifications";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { mergeAlertPlaceholdersWithData } from "../../utils/creativeJobAlertPlaceholders";
import { extractResizeResultUrls } from "../../utils/creativeJobResultUrls";
import {
  markCreativeJobResultsPersisted,
  startPersistCreativeJobResults,
} from "../../actions/creativeJobs";

const PAGE_SIZE = 10;
const INITIAL_DIMENSIONS_BATCH = 0;
// Delay so prepended loader cards commit before the gallery scrolls to them.
const GALLERY_LOADER_SCROLL_DELAY_MS = 100;

const getGalleryPreviewItemId = (entry) => entry?.id;

const isSameGalleryQueryContext = (left, right) =>
  left?.folderID === right?.folderID &&
  left?.filter === right?.filter &&
  left?.filterType === right?.filterType;

const haveSameContentIDs = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const rightSet = new Set(right.map((contentID) => String(contentID)));
  return left.every((contentID) => rightSet.has(String(contentID)));
};

const ModalGallery = ({
  modalOpen,
  multiple = true,
  onCloseModal = () => {},
  onCallbackSelectedItems = () => {},
  onValidateSelectedItems = null,
  showAdAccountOption = true,
  showGoogleDriveOption = true,
  showSocialPostsOption = true,
  closeActionsDisabled = false,
  skipAutoSelectOnUpload = false,
  platformConfigurations = {},
  pathStorage,
  pathRTDB,
  modeUpload = "sync",
  acceptedFormats = IMAGE_FORMATS.concat(VIDEO_FORMATS),
  minDimensions = {},
  showUploadDeviceOption = true,
  forceSocialPostsSource = false,
  initialValues = {},
  isOutsideCampaign = false,
  showFooterUseSelectedButton = false,
  maxSelections = null,
  fromCalendar = false,
  preselectedItems = [],
  enableAnimationAlertPlaceholder = false,
  enableCreativeJobAlertPlaceholders = false,
  dialogZIndex,
  initialContentReviewID = null,
  onInitialContentReviewConsumed = () => {},
  enableHistoryManagement = true,
  initialPreviewItemID = null,
  initialPreviewCarouselIndex = null,
  onInitialPreviewItemConsumed = () => {},
  initialGalleryEditAction = null,
  initialGalleryEditResize = null,
  onInitialGalleryEditConsumed = () => {},
}) => {
  const { openCreateWithIA, openVideoEditor, close } = useContentModals();
  const { openTemplatesList } = useTemplatesModals();
  const getInitialDataRef = useRef(null);
  const createWithIAIdRef = useRef(null);
  const videoEditorIdRef = useRef(null);
  const isPicker = !multiple;
  const [data, setData] = useState([]);
  const [isFetchingNewContents, setIsFetchingNewContents] = useState(false);
  const [selectPreview, setSelectPreview] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [folderBarHasHorizontalScroll, setFolderBarHasHorizontalScroll] =
    useState(false);

  // Initialize with preselected items for carousel mode
  useEffect(() => {
    if (isPicker) return;
    if (preselectedItems.length > 0 && data.length > 0) {
      // En modo carousel, solo preseleccionar el primer carousel encontrado
      const preselectedIndex = data.findIndex((d) => {
        // Buscar por URL del primer item del carousel preseleccionado
        return preselectedItems[0]?.url && d.url === preselectedItems[0].url;
      });

      if (preselectedIndex !== -1) {
        const preselectedId = getGalleryItemSelectionId(data[preselectedIndex]);
        if (preselectedId) {
          setSelectedItems([preselectedId]);
        }
      }
    }
  }, [preselectedItems, data, isPicker]);

  useEffect(() => {
    return () => {
      if (createWithIAIdRef.current) {
        close(createWithIAIdRef.current);
        createWithIAIdRef.current = null;
      }
      if (videoEditorIdRef.current) {
        close(videoEditorIdRef.current);
        videoEditorIdRef.current = null;
      }
    };
  }, [close]);

  // Convert selectedItems to Set for O(1) search instead of O(n)
  const selectedItemsSet = useMemo(() => {
    return new Set(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    setSelectedItems((prev) => {
      if (prev.length === 0) return prev;

      const existingIds = new Set(
        data.map((item) => getGalleryItemSelectionId(item)).filter(Boolean)
      );
      const next = prev.filter((selectionId) => existingIds.has(selectionId));
      return next.length === prev.length ? prev : next;
    });
  }, [data]);

  const [selectedSource, setSelectedSource] = useState(
    forceSocialPostsSource ? "socialPosts" : "myContent"
  );
  const [isLoadingSource, setIsLoadingSource] = useState(() =>
    Boolean(modalOpen)
  );
  const [isCalling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCreatingCarousel, setIsCreatingCarousel] = useState(false);
  const isCreatingCarouselRef = useRef(false);
  const [isOpenCanva, setIsOpenCanva] = useState(false);
  const [canvaImageData, setCanvaImageData] = useState(null);
  const [isOpenImageEditModal, setIsOpenImageEditModal] = useState(false);
  const [imageEditData, setImageEditData] = useState(null);
  const [isOpenBatchImageEditModal, setIsOpenBatchImageEditModal] =
    useState(false);
  const [batchEditItems, setBatchEditItems] = useState([]);
  const [isOpenSocialPostsModal, setIsOpenSocialPostsModal] = useState(false);
  const [isOpenAdAccountModal, setIsOpenAdAccountModal] = useState(false);
  const [
    isOpenGoogleDrivePickerAccountModal,
    setIsOpenGoogleDrivePickerAccountModal,
  ] = useState(false);
  const [googleDriveLoadingPhase, setGoogleDriveLoadingPhase] = useState(null);
  const isLoadingGoogleDrivePicker = googleDriveLoadingPhase !== null;
  const [isOpenCanvaGalleryModal, setIsOpenCanvaGalleryModal] = useState(false);
  const [canvaCreateTriggerId] = useState(() => getUniqueID());
  const [canvaGalleryTriggerId] = useState(() => getUniqueID());
  const [googleDriveImportTriggerId] = useState(() => getUniqueID());
  const [uploadInputId] = useState(() => getUniqueID());
  const [folderUploadInputId] = useState(() => getUniqueID());
  const pendingUploadFolderIDRef = useRef(null);
  const [isOpenCalendarModal, setIsOpenCalendarModal] = useState(false);
  const [calendarNavigateRequest, setCalendarNavigateRequest] = useState(null);
  const [filters, setFilters] = useState({
    contentType: ALL_FILTER,
    dimensions: ALL_FILTER,
    dateRange: ALL_FILTER,
    folderID: ALL_FILTER,
    approvalStatus: ALL_FILTER,
  });
  const onFolderFilterChange = useCallback((nextFolderFilterID) => {
    setFilters((prev) => ({
      ...prev,
      folderID: nextFolderFilterID,
      approvalStatus: ALL_FILTER,
    }));
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);
  const [showRatings, setShowRatings] = useState(false);
  const [enablingContentApprovalId, setEnablingContentApprovalId] =
    useState(null);
  const [contentReviewState, setContentReviewState] = useState({
    open: false,
    index: null,
    approvalID: null,
    reviewContentIDs: [],
    openedContentID: null,
    reviewMode: "reviewable",
  });
  const [reviewApprovalData, setReviewApprovalData] = useState(null);
  const [isLoadingReviewApproval, setIsLoadingReviewApproval] = useState(false);
  const [contentReviewBatchItems, setContentReviewBatchItems] = useState([]);
  const [contentApprovalListener, setContentApprovalListener] = useState(null);
  const [isSavingContentReview, setIsSavingContentReview] = useState(false);
  const [isReplacePickerForReviewOpen, setIsReplacePickerForReviewOpen] =
    useState(false);
  const [approvalSuccessDialog, setApprovalSuccessDialog] = useState({
    open: false,
    approvalUrl: "",
    approvalID: "",
    reviewerID: "",
  });
  const replacePickerCallbackRef = useRef(null);
  const reviewEditCallbackRef = useRef(null);
  const contentReviewFetchAttemptedRef = useRef(false);
  const openedContentReviewIDRef = useRef(null);

  // Memoized filter values to avoid duplicated logic
  const { activeFilter, activeFilterType } = useMemo(() => {
    // Exclusive filter: resolved by the database (indexed child "approvalStatus").
    if (filters.approvalStatus !== ALL_FILTER) {
      return {
        activeFilter: filters.approvalStatus,
        activeFilterType: "approvalStatus",
      };
    }

    const hasContentType = filters.contentType !== ALL_FILTER;
    const hasDimensions = filters.dimensions !== ALL_FILTER;

    if (hasContentType && hasDimensions) {
      const combinedFilter = `${filters.dimensions}_${filters.contentType}`;
      return {
        activeFilter: combinedFilter,
        activeFilterType: "formatAndType",
      };
    }

    if (hasContentType) {
      return { activeFilter: filters.contentType, activeFilterType: "type" };
    }

    if (hasDimensions) {
      return { activeFilter: filters.dimensions, activeFilterType: "format" };
    }

    return { activeFilter: null, activeFilterType: null };
  }, [filters.approvalStatus, filters.contentType, filters.dimensions]);

  const navigate = useNavigate();
  const location = useLocation();

  const onCloseGalleryModal = useCallback(() => {
    if (createWithIAIdRef.current) {
      close(createWithIAIdRef.current);
      createWithIAIdRef.current = null;
    }
    if (videoEditorIdRef.current) {
      close(videoEditorIdRef.current);
      videoEditorIdRef.current = null;
    }

    onCloseModal();

    if (!enableHistoryManagement && location.pathname === ROUTE_GALLERY) {
      const backgroundLocation = location.state?.galleryBackgroundLocation;
      if (
        backgroundLocation?.pathname &&
        backgroundLocation.pathname !== ROUTE_GALLERY
      ) {
        navigate(
          {
            pathname: backgroundLocation.pathname,
            search: backgroundLocation.search || "",
            hash: backgroundLocation.hash || "",
          },
          { replace: true, state: backgroundLocation.state }
        );
        return;
      }

      navigate(ROUTE_CAMPAIGNS, { replace: true });
    }
  }, [
    close,
    enableHistoryManagement,
    location.pathname,
    location.state,
    navigate,
    onCloseModal,
  ]);

  const { activeAlerts: notifications } = useNotifications();
  const prevLoadingCountRef = useRef(0);
  const refreshScheduledContentsRef = useRef(null);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const dataRef = useRef(data);
  dataRef.current = data;
  const shouldMergeAlertPlaceholders =
    enableAnimationAlertPlaceholder || enableCreativeJobAlertPlaceholders;
  const galleryFetchGenerationRef = useRef(0);
  const galleryQueryContextRef = useRef({
    folderID: null,
    filter: null,
    filterType: null,
  });
  const getNextMediaTimerRef = useRef(null);
  const { scheduledDate, clearScheduledDate } = useScheduledDate();

  // Only use the date if it comes from the calendar
  const dateToUse = fromCalendar ? scheduledDate : null;

  const onHandleCreateCampaign = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const initialCreativesData = buildInitialCreativesFromGalleryItems(
      getGalleryItemsBySelectionIds(data, selectedItems)
    );

    if (initialCreativesData.length === 0) {
      return;
    }

    const shouldConfirmCreateCampaignLeave =
      !isOutsideCampaign ||
      isPicker ||
      showFooterUseSelectedButton ||
      !closeActionsDisabled;

    if (shouldConfirmCreateCampaignLeave) {
      const result = await ConfirmAlert({
        title: t(GALLERY_CREATE_CAMPAIGN_LEAVE_FLOW_TITLE),
        text: t(GALLERY_CREATE_CAMPAIGN_LEAVE_FLOW_DESCRIPTION),
        confirmButtonText: t(BUTTON_CREATE_CAMPAIGN2),
        cancelButtonText: t(BUTTON_CANCEL),
      });

      if (!result?.isConfirmed) {
        return;
      }
    }

    const createCampaignUrl = buildCreateCampaignListUrl({
      initialCreatives: encodeURIComponent(
        JSON.stringify(initialCreativesData)
      ),
    });

    const isGalleryRouteFlow =
      !enableHistoryManagement && location.pathname === ROUTE_GALLERY;

    if (isGalleryRouteFlow) {
      navigate(createCampaignUrl, { replace: true });
      return;
    }

    onCloseModal();
    navigate(createCampaignUrl);
  };

  const { t } = useTranslationApp();
  const {
    ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
    ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
  } = useCreditActionCosts();

  const { requestAnimateImage, previewModalProps } =
    useAnimateImageWithPreview();

  const onRegisterCalendarRefresh = useCallback((fn) => {
    refreshScheduledContentsRef.current = fn ?? null;
  }, []);

  const onRefreshCalendarFromGallery = useCallback(() => {
    refreshScheduledContentsRef.current?.(false);
  }, []);

  const onNavigateToScheduledConsumed = useCallback(() => {
    setCalendarNavigateRequest(null);
  }, []);

  const onScheduledShareSuccessFromGallery = useCallback(
    ({ scheduledDate }) => {
      if (!scheduledDate) return;
      setCalendarNavigateRequest({ id: Date.now(), date: scheduledDate });
      setIsOpenCalendarModal(true);
    },
    []
  );

  const { integrations } = useSelectorApp((state) => state.user);

  const { rowsPerPage, lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: () => {},
  });

  const getGalleryQueryContext = useCallback(
    () => ({
      folderID: filters.folderID === ALL_FILTER ? null : filters.folderID,
      filter: activeFilter,
      filterType: activeFilterType,
    }),
    [filters.folderID, activeFilter, activeFilterType]
  );

  const onCancelGetNextMedia = useCallback(() => {
    if (getNextMediaTimerRef.current) {
      clearTimeout(getNextMediaTimerRef.current);
      getNextMediaTimerRef.current = null;
    }
  }, []);

  const platformConfigs = usePlatformConfigurations(platformConfigurations);

  const {
    meta: {
      integrationID: finalMetaIntegrationID,
      adAccountID: finalMetaAdAccountID,
      pageID: finalMetaPageID,
    },
    linkedIn: {
      integrationID: finalLinkedInIntegrationID,
      adAccountID: finalLinkedInAdAccountID,
      publishAs: finalLinkedInAuthorType,
    },
  } = platformConfigs;

  const isForceLinkedInPostsSource =
    forceSocialPostsSource && initialValues?.platform === LINKEDIN;

  const googleIntegrations = integrations?.google || {};
  const metaIntegrations = integrations?.facebook || {};
  const linkedInIntegrations = integrations?.linkedIn || {};

  const hasGoogleIntegration = Object.keys(googleIntegrations).length > 0;
  const hasMetaIntegration = Object.keys(metaIntegrations).length > 0;
  const hasLinkedInIntegration = Object.keys(linkedInIntegrations).length > 0;
  const hasSocialPostsIntegration =
    hasMetaIntegration || hasLinkedInIntegration;
  const hasAdAccountIntegration = hasGoogleIntegration || hasMetaIntegration;

  const galleryRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const [highlightedContentIds, setHighlightedContentIds] = useState([]);

  // Scrolls via the helper (not scrollIntoView) so the scroll still lands when
  // the virtualized list reflows mid-animation (placeholders prepended).
  const scrollGalleryToAddContentButtons = useCallback(() => {
    const container = galleryRef.current;
    const getTarget = () =>
      getCenteredOffsetInContainer(
        container,
        container?.querySelector('[data-testid="add-content-buttons"]')
      );
    smoothScrollContainerTo(container, getTarget);
  }, []);

  const scrollGalleryToLoadersAfterConfirm = useCallback(() => {
    setTimeout(() => {
      scrollGalleryToAddContentButtons();
    }, GALLERY_LOADER_SCROLL_DELAY_MS);
  }, [scrollGalleryToAddContentButtons]);

  const notifyGalleryContentSaved = useCallback(
    ({ contentIds = [], scroll = true } = {}) => {
      if (contentIds.length > 0) {
        setHighlightedContentIds(contentIds.map(String));
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedContentIds([]);
        }, 5000);
      }

      if (scroll) {
        setTimeout(() => {
          smoothScrollContainerTo(galleryRef.current, 0);
        }, 50);
      }
    },
    []
  );

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "600px",
    once: false,
    disabledTimeout: true,
    scrollRootRef: galleryRef,
  });

  const dispatch = useDispatchApp();

  const refreshGalleryPublicationSources = useCallback(
    async ({ contentIDs = [] } = {}) => {
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];
      if (uniqueContentIDs.length === 0) return;

      const response = await dispatch(
        startGetContentItemsByIds({ contentIDs: uniqueContentIDs })
      );
      if (!response?.ok || !Array.isArray(response.items)) return;

      setData((prev) => mergePublishedIntoGalleryItems(prev, response.items));
    },
    [dispatch]
  );

  const onShareSuccessFromGallery = useCallback(
    ({ contentIDs } = {}) => refreshGalleryPublicationSources({ contentIDs }),
    [refreshGalleryPublicationSources]
  );

  const loadedContentIDs = useMemo(
    () =>
      data
        .map((item) => item?.id)
        .filter(
          (id) => id && !String(id).startsWith(BATCH_RESIZE_PLACEHOLDER_PREFIX)
        )
        .map(String),
    [data]
  );

  const onCloseCalendarModal = useCallback(() => {
    setIsOpenCalendarModal(false);
    refreshGalleryPublicationSources({ contentIDs: loadedContentIDs });
  }, [loadedContentIDs, refreshGalleryPublicationSources]);

  const galleryPermissions = useGalleryPermissions();
  const canEditGalleryFolders = galleryPermissions.canEditGallery;

  const {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    stampFolderContent,
    onSelectFolder,
    onDropMany,
    moveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  } = useGalleryFolderSession({
    modalOpen,
    folderFilterID: filters.folderID,
    onFolderFilterChange,
    setData,
    clearSelection,
  });

  useEffect(() => {
    dispatch(setModalOpenState("isModalGallery", modalOpen));
    return () => {
      dispatch(setModalOpenState("isModalGallery", false));
      // Limpiar la fecha cuando se cierra ModalGallery solo si viene del calendario
      if (!modalOpen && fromCalendar) {
        clearScheduledDate();
      }
    };
  }, [modalOpen, dispatch, clearScheduledDate, fromCalendar]);

  const { itemDimensions } = useMultipleMediaDimensions(
    useMemo(() => {
      return data.slice(0, INITIAL_DIMENSIONS_BATCH);
    }, [data])
  );

  const translatedContentTypeOptions = contentTypeOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const translatedApprovalStatusOptions = [
    { value: ALL_FILTER, label: t(GALLERY_FILTER_APPROVAL_STATUS) },
    { value: "pending", label: t(CAMPAIGN_APPROVAL_STATUS_PENDING) },
    { value: "rejected", label: t(CAMPAIGN_APPROVAL_STATUS_REJECTED) },
    { value: "approved", label: t(CAMPAIGN_APPROVAL_STATUS_APPROVED) },
  ];

  const translatedDimensionsOptions = dimensionsOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  const filterSelectSx = {
    minWidth: 160,
  };

  // Folder queries use the folder index, so the remaining filters run locally.
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const hasFolderFilter = filters.folderID !== ALL_FILTER;
    const hasContentTypeFilter =
      hasFolderFilter && filters.contentType !== ALL_FILTER;
    const hasDimensionsFilter =
      hasFolderFilter && filters.dimensions !== ALL_FILTER;
    const hasDateRangeFilter = filters.dateRange !== ALL_FILTER;

    if (!hasContentTypeFilter && !hasDimensionsFilter && !hasDateRangeFilter) {
      return data;
    }

    return data.filter((item) => {
      if (hasContentTypeFilter && item.type !== filters.contentType) {
        return false;
      }

      if (hasDimensionsFilter && item.format !== filters.dimensions) {
        return false;
      }

      if (!hasDateRangeFilter) return true;

      const itemDate = new Date(item.creationTime);
      const now = new Date();
      const diffTime = now - itemDate;
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      switch (filters.dateRange) {
        case FILTERS_MEDIA_DATE_RANGE.today:
          if (diffDays > 1) return false;
          break;
        case FILTERS_MEDIA_DATE_RANGE.week:
          if (diffDays > 7) return false;
          break;
        case FILTERS_MEDIA_DATE_RANGE.month:
          if (diffDays > 30) return false;
          break;
        case FILTERS_MEDIA_DATE_RANGE.year:
          if (diffDays > 365) return false;
          break;
      }

      return true;
    });
  }, [
    data,
    filters.contentType,
    filters.dateRange,
    filters.dimensions,
    filters.folderID,
  ]);

  // The approval status filter is exclusive: it clears every other filter and
  // any other filter clears it.
  const onFilterChange = (filterType, value) => {
    setFilters((prev) => {
      if (filterType === "approvalStatus") {
        return {
          contentType: ALL_FILTER,
          dimensions: ALL_FILTER,
          dateRange: ALL_FILTER,
          folderID: ALL_FILTER,
          approvalStatus: value,
        };
      }

      return {
        ...prev,
        [filterType]: value,
        approvalStatus: ALL_FILTER,
      };
    });
  };

  const resetFilters = () => {
    setFilters({
      contentType: ALL_FILTER,
      dimensions: ALL_FILTER,
      dateRange: ALL_FILTER,
      folderID: ALL_FILTER,
      approvalStatus: ALL_FILTER,
    });
    setSelectedItems([]);
  };

  const onLoadLastItem = useCallback(() => setLoadLastItem(true), []);

  const onCallbackResizeFormat = useCallback(
    async ({
      index,
      format,
      formats,
      width,
      height,
      isCarousel,
      carouselUrls,
      showPlaceholder = false,
      onCreditsConfirmed,
    }) => {
      const resolvedFormats = Array.isArray(formats)
        ? formats
        : format
          ? [format]
          : [];

      // Usar filteredData en lugar de dataFiltered ya que está definido antes
      const currentFilteredData = filteredData;
      const currentItem = currentFilteredData[index];

      // Detectar si es carousel
      const isCarouselItem =
        isCarousel !== undefined
          ? isCarousel
          : currentItem?.isCarousel &&
            currentItem?.carouselImages &&
            currentItem.carouselImages.length > 1;

      // Determinar URLs a enviar
      let urlsToResize = [];
      if (isCarouselItem) {
        urlsToResize =
          carouselUrls ||
          (currentItem.carouselImages || []).map((img) => img.url || img);
      } else {
        urlsToResize = [currentItem.url];
      }

      const formatsCount = resolvedFormats.length || 1;

      // Calcular multiplicador de costo
      const multiplier =
        (isCarouselItem ? urlsToResize.length : 1) * formatsCount;

      // Verificar bloqueo de acciones con multiplicador (ÚNICA verificación de costo)
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          multiplier: multiplier,
          showUsageConfirmationAlert: true,
        })
      );
      if (isBlocked) {
        return { cancelled: true };
      }

      if (typeof onCreditsConfirmed === "function") {
        onCreditsConfirmed();
      }

      const itemId = currentItem?.id;
      const primaryFormat = resolvedFormats[0] || format;
      const isMultiFormat = resolvedFormats.length > 1;
      const shouldUsePlaceholderItems =
        !isCarouselItem &&
        itemId &&
        resolvedFormats.length > 0 &&
        (showPlaceholder || isMultiFormat);

      let placeholderIds = [];

      if (shouldUsePlaceholderItems) {
        placeholderIds = resolvedFormats.map(() => getUniqueID());
        setData((prev) => {
          const placeholders = resolvedFormats.map(
            (targetFormat, formatIndex) => ({
              ...getResizingFormatItemPatch({
                format: targetFormat,
                width,
                height,
              }),
              id: placeholderIds[formatIndex],
              creationTime: new Date().getTime(),
              creationTimeNegative: -new Date().getTime(),
              url: placeholderIds[formatIndex],
            })
          );

          return [...placeholders, ...prev];
        });
      } else if (!showPlaceholder && itemId) {
        // Un solo formato desde GalleryItem: placeholder sobre el item original
        setData((prev) => {
          return prev.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  ...getResizingFormatItemPatch({
                    format: primaryFormat,
                    width,
                    height,
                  }),
                }
              : item
          );
        });
      }

      const removePlaceholderItems = (items) => {
        if (placeholderIds.length === 0) return items;

        const idsToRemove = new Set(placeholderIds);
        return items.filter((item) => !idsToRemove.has(item.id));
      };

      try {
        const response = await dispatch(
          startResizeImage(
            buildResizeImagePayload({
              urls: urlsToResize,
              formats: resolvedFormats,
              format: primaryFormat,
              width,
              height,
            })
          )
        );

        if (response.ok) {
          const persistResizedUrls = async ({ urlsToPersist, jobID }) => {
            const contents = urlsToPersist.filter(Boolean).map((newUrl) =>
              stampFolderContent({
                url: newUrl,
                from: "upload",
                userID: null,
              })
            );

            if (contents.length === 0) {
              return [];
            }

            if (jobID) {
              const responseSaved = await dispatch(
                startPersistCreativeJobResults({ jobID, contents })
              );
              return responseSaved?.data
                ? Object.values(responseSaved.data)
                : [];
            }

            const savedItems = [];
            for (const content of contents) {
              const responseSavedImage = await dispatch(
                startAddContentsToList([content])
              );
              if (responseSavedImage) {
                savedItems.push(Object.values(responseSavedImage)[0]);
              }
            }
            return savedItems;
          };

          const resizedUrls = extractResizeResultUrls(response);
          const jobID = response?.jobID;

          if (isCarouselItem) {
            if (resizedUrls.length > 0) {
              const carouselContent = stampFolderContent({
                type: "carousel",
                urls: resizedUrls,
                isAIGenerated: false,
              });

              const responseSavedCarousel = jobID
                ? await dispatch(
                    startPersistCreativeJobResults({
                      jobID,
                      contents: [carouselContent],
                    })
                  )
                : await dispatch(startAddContentsToList([carouselContent]));

              const savedCarouselData = jobID
                ? responseSavedCarousel?.data
                : responseSavedCarousel;

              if (savedCarouselData) {
                const newContentFormatted = Object.values(savedCarouselData)[0];
                const formattedCarousels = groupGalleryCarousels([
                  newContentFormatted,
                ]);
                const formattedCarousel =
                  formattedCarousels.length > 0 ? formattedCarousels[0] : null;

                if (formattedCarousel) {
                  setData((prev) => {
                    let updated = removePlaceholderItems([...prev]);
                    if (itemId) {
                      updated = updated.map((item) =>
                        item.id === itemId
                          ? { ...item, ...clearResizingFormatItemPatch() }
                          : item
                      );
                    }
                    return [formattedCarousel, ...updated];
                  });
                }
              }
            }
          } else {
            const savedItems = await persistResizedUrls({
              urlsToPersist: resizedUrls,
              jobID,
            });

            if (savedItems.length > 0) {
              setData((prev) => {
                let updated = removePlaceholderItems(prev);
                if (itemId && !shouldUsePlaceholderItems) {
                  updated = updated.map((item) =>
                    item.id === itemId
                      ? { ...item, ...clearResizingFormatItemPatch() }
                      : item
                  );
                }
                return [...savedItems, ...updated];
              });
            }
          }
        }
      } finally {
        if (placeholderIds.length > 0) {
          setData((prev) => removePlaceholderItems(prev));
        } else if (itemId && !showPlaceholder && !isMultiFormat) {
          setData((prev) => {
            return prev.map((item) =>
              item.id === itemId
                ? { ...item, ...clearResizingFormatItemPatch() }
                : item
            );
          });
        }
      }
      return { success: true };
    },
    // eslint-disable-next-line
    [stampFolderContent, filteredData, dispatch]
  );

  const onCallbackAnimateImage = useCallback(
    async ({ index, prompt, outroVideoUrl }) => {
      const currentFilteredData = filteredData;
      const currentItem = currentFilteredData[index];
      if (!currentItem?.url) return;

      const animationMeta = {};
      if (currentItem.width && currentItem.height) {
        animationMeta.width = currentItem.width;
        animationMeta.height = currentItem.height;
      }

      const response = await dispatch(
        startAnimateImage({
          imageUrl: currentItem.url,
          ...(prompt ? { prompt } : {}),
          ...(outroVideoUrl ? { outroVideoUrl } : {}),
          notificationContext: {
            source: "modalGallery",
            generationDataBase: animationMeta,
          },
        })
      );

      if (response?.ok && response?.data?.url) {
        const content = stampFolderContent({
          url: response.data.url,
          from: "upload",
          userID: null,
        });

        const responseSavedImage = response?.jobID
          ? await dispatch(
              startPersistCreativeJobResults({
                jobID: response.jobID,
                contents: [content],
              })
            )
          : await dispatch(startAddContentsToList([content]));

        const savedData = response?.jobID
          ? responseSavedImage?.data
          : responseSavedImage;

        if (savedData) {
          const newContentFormatted = Object.values(savedData)[0];
          setData((prev) => {
            return [newContentFormatted, ...prev];
          });
        }
      }
    },
    [stampFolderContent, filteredData, dispatch]
  );

  const onResizeFromModal = useCallback(
    async ({
      index,
      format,
      formats,
      width,
      height,
      isCarousel,
      carouselUrls,
      onCreditsConfirmed,
    }) => {
      const currentFilteredData = filteredData;
      const currentItem = currentFilteredData[index];

      // Detectar si es carousel
      const isCarouselItem =
        isCarousel !== undefined
          ? isCarousel
          : currentItem?.isCarousel &&
            currentItem?.carouselImages &&
            currentItem.carouselImages.length > 1;

      setSelectPreview(null);

      try {
        // Cost is confirmed first. Placeholders (and the scroll to them) run only after confirm.
        const result = await onCallbackResizeFormat({
          index,
          format,
          formats,
          width,
          height,
          isCarousel: isCarouselItem,
          carouselUrls,
          showPlaceholder: true,
          onCreditsConfirmed: () => {
            onCreditsConfirmed?.();
            scrollGalleryToLoadersAfterConfirm();
          },
        });

        if (result?.cancelled) {
          return;
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "persistResizedUrls",
            capture: "exception",
          })
        );
        console.error("Error en resize:", error);
      }
    },
    [
      dispatch,
      onCallbackResizeFormat,
      filteredData,
      scrollGalleryToLoadersAfterConfirm,
    ]
  );

  const onBatchResizeFormat = useCallback(
    async ({ formats, width, height, onCreditsConfirmed }) => {
      const eligibleItems = getBatchResizeEligibleItems(data, selectedItems);
      if (eligibleItems.length === 0) {
        return { cancelled: true };
      }

      const multiplier = await estimateBatchResizeCreditCount({
        items: eligibleItems,
        formats,
      });
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          multiplier,
        })
      );
      if (isBlocked) {
        return { cancelled: true };
      }

      onCreditsConfirmed?.();

      const requestedFormats = Array.isArray(formats) ? formats : [];
      const now = Date.now();
      const placeholders = requestedFormats.map((targetFormat, formatIndex) => {
        const placeholderId = getBatchResizePlaceholderId(targetFormat);
        const creationTime = now + (requestedFormats.length - formatIndex);
        return {
          ...getResizingFormatItemPatch({
            format: targetFormat,
            width,
            height,
          }),
          id: placeholderId,
          url: placeholderId,
          creationTime,
          creationTimeNegative: -creationTime,
        };
      });

      setData((prev) => [
        ...placeholders,
        ...prev.filter((item) => !isBatchResizePlaceholder(item)),
      ]);
      if (placeholders.length > 0) {
        scrollGalleryToLoadersAfterConfirm();
      }

      try {
        const response = await dispatch(
          startResizeImagesBatch({
            items: eligibleItems,
            formats,
            width,
            height,
            persistToContentList: true,
            modalType: "gallery",
            folderID: activeFolderID,
            sourceImageCount: countBatchResizeSourceImages(eligibleItems),
            estimatedCredits: multiplier,
          })
        );

        const createdContents = response?.data?.createdContents;
        if (
          response?.ok &&
          Array.isArray(createdContents) &&
          createdContents.length > 0
        ) {
          const formattedContents = createdContents
            .map((item) => {
              if (item.type === "carousel" && Array.isArray(item.urls)) {
                const carouselImages = item.urls.map((url, index) => ({
                  url,
                  rating: null,
                  id: `${item.id}-${index}`,
                }));
                return {
                  ...item,
                  isCarousel: true,
                  carouselImages,
                  url: carouselImages[0]?.url,
                  rating: null,
                };
              }
              return item;
            })
            .sort(
              (left, right) =>
                (right.creationTime || 0) - (left.creationTime || 0)
            );

          setData((prev) => [
            ...formattedContents,
            ...prev.filter((item) => !isBatchResizePlaceholder(item)),
          ]);
          notifyGalleryContentSaved({
            contentIds: createdContents.map((item) => item.id).filter(Boolean),
          });

          if (response?.jobID) {
            await dispatch(
              markCreativeJobResultsPersisted({ jobID: response.jobID })
            );
          }
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "persistResizedUrls",
            capture: "exception",
          })
        );
        console.error("Error en resize batch:", error);
      } finally {
        setData((prev) =>
          prev.filter((item) => !isBatchResizePlaceholder(item))
        );
      }

      return { success: true };
    },
    [
      data,
      selectedItems,
      dispatch,
      ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
      activeFolderID,
      notifyGalleryContentSaved,
      scrollGalleryToLoadersAfterConfirm,
    ]
  );

  const onAnimateImageFromModal = useCallback(
    async ({ index }) => {
      const currentItem = filteredData[index];
      if (!currentItem?.url) return;

      requestAnimateImage({
        imageUrl: currentItem.url,
        onConfirm: async ({
          prompt = "",
          outroVideoUrl = "",
          closePreview,
        } = {}) => {
          const isBlocked = await dispatch(
            getIsBlockedActions({
              action: ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
              showUsageConfirmationAlert: true,
            })
          );
          if (isBlocked) {
            return;
          }
          closePreview?.();

          setSelectPreview(null);

          setTimeout(() => {
            scrollGalleryToAddContentButtons();
          }, 100);

          if (shouldMergeAlertPlaceholders) {
            await onCallbackAnimateImage({ index, prompt, outroVideoUrl });
            return;
          }

          const removeID = getUniqueID();

          const originalItem = filteredData[index];
          const placeholderDimensions =
            originalItem?.width && originalItem?.height
              ? {
                  width: originalItem.width,
                  height: originalItem.height,
                }
              : {};

          setData((prev) => {
            const newData = [
              {
                resizingFormat: "animate",
                id: removeID,
                creationTime: new Date().getTime(),
                creationTimeNegative: -new Date().getTime(),
                url: removeID,
                ...placeholderDimensions,
              },
              ...prev,
            ];
            return newData;
          });

          try {
            await onCallbackAnimateImage({ index, prompt, outroVideoUrl });
          } finally {
            setData((prev) => {
              const newData = [...prev];
              const newFilteredData = newData.filter(
                (item) => item.id !== removeID
              );
              return newFilteredData;
            });
          }
        },
      });
    },
    // eslint-disable-next-line
    [
      onCallbackAnimateImage,
      dispatch,
      filteredData,
      shouldMergeAlertPlaceholders,
      requestAnimateImage,
      ADS_CAMPAIGN_ANIMATE_IMAGE_ACTION,
      stampFolderContent,
    ]
  );
  const finalizeUploadedContents = async (
    contents,
    { skipAutoSelect = skipAutoSelectOnUpload } = {}
  ) => {
    const contextualContents = contents.map(stampFolderContent);

    if (modeUpload === "sync") {
      await dispatch(startAddContentsToList(contextualContents));
    }

    if (skipAutoSelect) {
      await refreshGalleryAfterImport();
      return;
    }

    if (onValidateSelectedItems) {
      const isValid = await onValidateSelectedItems(contextualContents);
      if (!isValid) {
        return;
      }
    }

    onCallbackSelectedItems(contextualContents);
    if (!closeActionsDisabled) {
      onCloseModal();
    } else {
      await refreshGalleryAfterImport();
    }
  };

  const ensureUploadFolder = useEnsureGalleryUploadFolder({
    folders,
    parentFolderID: createFolderParentID,
    onCreateFolder: onCreateFolderInContext,
  });

  const onDirectoryFileSelect = useCallback(
    async (files) => {
      const folderName = getSelectedDirectoryName(files);
      if (!folderName) {
        pendingUploadFolderIDRef.current = null;
        return;
      }

      const folderID = await ensureUploadFolder({ name: folderName });
      if (!folderID) {
        pendingUploadFolderIDRef.current = null;
        return false;
      }

      pendingUploadFolderIDRef.current = folderID;
    },
    [ensureUploadFolder]
  );

  const onOpenDeviceUpload = useCallback(
    async ({ directory = false } = {}) => {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
          showPaywall: true,
        })
      );
      if (isBlocked) return;

      const inputId = directory ? folderUploadInputId : uploadInputId;
      document.getElementById(inputId)?.click();
    },
    [dispatch, folderUploadInputId, uploadInputId]
  );

  const onAddContent = async (newMedias) => {
    const folderID = pendingUploadFolderIDRef.current;
    pendingUploadFolderIDRef.current = null;

    const contents = newMedias.map((media) => ({
      url: media,
      from: "upload",
      userID: null,
      ...(folderID ? { folderID } : {}),
    }));

    await finalizeUploadedContents(contents, { skipAutoSelect: true });

    if (folderID && folderID !== activeFolderID) {
      onSelectFolder(folderID);
    }
  };

  const onImportFromGoogleDrive = async ({ integrationID }) => {
    if (!integrationID) return;

    try {
      setGoogleDriveLoadingPhase("configuring");

      const tokenResult = await dispatch(
        startGetDriveAccessTokenForPicker({ integrationID })
      );
      if (!tokenResult.ok) {
        setGoogleDriveLoadingPhase(null);
        return;
      }

      await loadGoogleScripts();

      openGoogleDrivePicker({
        accessToken: tokenResult.data.accessToken,
        onCancel: () => {
          setGoogleDriveLoadingPhase(null);
        },
        onPicked: async (files) => {
          if (!files?.length) {
            setGoogleDriveLoadingPhase(null);
            return;
          }

          setGoogleDriveLoadingPhase("importing");

          try {
            const saveResult = await dispatch(
              startSaveGoogleDriveFilesInStorage({
                integrationID,
                path: pathStorage,
                files,
              })
            );

            if (!saveResult.ok) return;

            const urls = saveResult.data?.urls || [];
            const contents = urls.map((url) => ({
              url,
              from: "googleDrive",
              userID: null,
            }));

            await finalizeUploadedContents(contents);
          } finally {
            setGoogleDriveLoadingPhase(null);
          }
        },
      });

      setGoogleDriveLoadingPhase(null);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onImportFromGoogleDrive",
          capture: "exception",
        })
      );
      console.error(error);
      setGoogleDriveLoadingPhase(null);
    }
  };

  const openCanvaWithImage = useCallback((imageData) => {
    setCanvaImageData({
      url: imageData.url,
      dimensions: imageData.dimensions,
      index: imageData.index,
      ...(imageData.isCarousel && {
        carouselIndex: imageData.carouselIndex,
        isCarousel: true,
        itemId: imageData.itemId,
      }),
    });
    setIsOpenCanva(true);
  }, []);

  const onCloseCanvaModal = () => {
    setIsOpenCanva(false);
    setCanvaImageData(null);
  };

  const openImageEditWithIA = useCallback((imageData) => {
    setImageEditData({
      url: imageData.url,
      dimensions: imageData.dimensions,
      index: imageData.index,
      itemId: imageData.itemId,
      ...(imageData.isCarousel && {
        carouselIndex: imageData.carouselIndex,
        isCarousel: true,
      }),
    });
    setIsOpenImageEditModal(true);
  }, []);

  const onCallbackSaveVideoEdit = useCallback(
    async (result) => {
      if (!result?.ok) return;

      if (result.mode === "replace" && result.item?.id) {
        const timestamps = getGalleryBumpTimestamps();
        setData((prev) =>
          applyGalleryReorderPreservingPlaceholders(prev, (rest) =>
            bumpGalleryItemToFront(rest, result.item.id, {
              url: result.url || result.item.url,
              sourceUrl: result.item.sourceUrl || result.sourceUrl,
              originalVideoUrl: null,
              subtitles: result.item.subtitles,
              duration: result.item.duration,
              ...timestamps,
            })
          )
        );
        notifyGalleryContentSaved({ contentIds: [result.item.id] });
        return;
      }

      const newContentFormatted = result.item;
      if (newContentFormatted) {
        setData((prev) => [newContentFormatted, ...prev]);
        notifyGalleryContentSaved({ contentIds: [newContentFormatted.id] });
      }
    },
    [notifyGalleryContentSaved]
  );

  const openVideoEditorFromItem = useCallback(
    (videoData) => {
      const videoEditorId = openVideoEditor({
        sourceUrl:
          videoData.sourceUrl || videoData.originalVideoUrl || videoData.url,
        subtitles: videoData.subtitles || null,
        sourceAssetID: videoData.itemId,
        sourceName: videoData.name,
        pathStorage,
        onCallbackSave: onCallbackSaveVideoEdit,
        replaceExisting: true,
        onCloseModal: () => {
          videoEditorIdRef.current = null;
        },
      });
      videoEditorIdRef.current = videoEditorId;
    },
    [onCallbackSaveVideoEdit, openVideoEditor, pathStorage]
  );

  const onCloseImageEditModal = () => {
    setIsOpenImageEditModal(false);
    setImageEditData(null);
    reviewEditCallbackRef.current = null;
  };

  const onOpenBatchImageEditModal = useCallback(() => {
    const snapshotItems = getBatchEditEligibleItems(data, selectedItems);
    if (snapshotItems.length === 0) return;
    setBatchEditItems(snapshotItems);
    setIsOpenBatchImageEditModal(true);
  }, [data, selectedItems]);

  const onCreateCarouselFromSelection = useCallback(async () => {
    if (isCreatingCarouselRef.current) return;

    const eligibleItems = getCarouselCreationEligibleItems(data, selectedItems);
    const urls = eligibleItems.map((item) => item.url).filter(Boolean);
    if (eligibleItems.length === 0 || urls.length !== eligibleItems.length) {
      return;
    }

    isCreatingCarouselRef.current = true;
    setIsCreatingCarousel(true);
    try {
      const carouselContent = stampFolderContent({
        type: "carousel",
        urls,
        isAIGenerated: false,
      });

      const responseSavedCarousel = await dispatch(
        startAddContentsToList([carouselContent])
      );

      if (!responseSavedCarousel) {
        return;
      }

      const newContentFormatted = Object.values(responseSavedCarousel)[0];
      if (!newContentFormatted) {
        return;
      }

      const formattedCarousels = groupGalleryCarousels([newContentFormatted]);
      const formattedCarousel = formattedCarousels[0];

      if (!formattedCarousel) {
        return;
      }

      setData((prev) => [formattedCarousel, ...prev]);
      setSelectedItems([]);
      notifyGalleryContentSaved({
        contentIds: [formattedCarousel.id].filter(Boolean),
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onCloseImageEditModal",
          capture: "exception",
        })
      );
      console.error("Error creating carousel from selection:", error);
    } finally {
      isCreatingCarouselRef.current = false;
      setIsCreatingCarousel(false);
    }
  }, [
    data,
    dispatch,
    notifyGalleryContentSaved,
    selectedItems,
    stampFolderContent,
  ]);

  const onCloseBatchImageEditModal = useCallback(() => {
    setIsOpenBatchImageEditModal(false);
    setBatchEditItems([]);
  }, []);

  const onApplyBatchImageEditResults = useCallback(
    async ({ mode, items: successfulItems }) => {
      if (!Array.isArray(successfulItems) || successfulItems.length === 0) {
        return;
      }

      if (mode === "new") {
        const contents = successfulItems.map(({ url }) => ({
          url,
          from: "ai",
          userID: null,
        }));

        const responseSavedImages = await dispatch(
          startAddContentsToList(contents)
        );

        if (responseSavedImages) {
          const newItems = Object.values(responseSavedImages);
          setData((prev) => [...newItems, ...prev]);
          notifyGalleryContentSaved({
            contentIds: newItems.map((item) => item.id),
          });
        }
        return;
      }

      const replaceUpdates = successfulItems
        .map(({ selectionId, url }) => {
          const existingItem = findGalleryItemBySelectionId(data, selectionId);
          if (!existingItem?.id) {
            return null;
          }
          return { id: existingItem.id, patch: { url } };
        })
        .filter(Boolean);

      if (replaceUpdates.length === 0) {
        return;
      }

      setData((prev) =>
        applyGalleryReorderPreservingPlaceholders(prev, (rest) =>
          bumpGalleryItemsToFront(rest, replaceUpdates)
        )
      );

      replaceUpdates.forEach(({ id, patch }) => {
        dispatch(
          startBumpAndUpdateContentInList({
            id,
            url: patch.url,
          })
        ).catch((error) => {
          dispatch(
            LaunchError(error, false, [], {
              functionName: "onCloseImageEditModal",
              capture: "exception",
            })
          );
          console.error("Error updating batch edited content:", error);
        });
      });

      notifyGalleryContentSaved({
        contentIds: replaceUpdates.map((update) => update.id),
      });
    },
    [data, dispatch, notifyGalleryContentSaved]
  );

  const mergePlaceholdersWithData = useCallback(
    (dataItems, currentNotifications) =>
      mergeAlertPlaceholdersWithData({
        dataItems,
        notifications: currentNotifications,
      }),
    []
  );

  const getInitialData = async () => {
    onCancelGetNextMedia();
    galleryFetchGenerationRef.current += 1;
    const fetchGeneration = galleryFetchGenerationRef.current;
    const queryContext = getGalleryQueryContext();
    galleryQueryContextRef.current = queryContext;

    setIsLoadingSource(true);
    setIsFetchingNewContents(false);
    setLoadLastItem(false);
    setData([]);
    onChangeLastKey(undefined);

    const guardedOnChangeLastKey = (key) => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onChangeLastKey(key);
      }
    };

    const guardedOnLoadLastItem = () => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onLoadLastItem();
      }
    };

    const response = await dispatch(
      startGetGalleryContent({
        lastKey: undefined,
        onChangeLastKey: guardedOnChangeLastKey,
        limit: rowsPerPage,
        onLoadLastItem: guardedOnLoadLastItem,
        filter: queryContext.filter,
        filterType: queryContext.filterType,
        folderID: queryContext.folderID,
      })
    );

    if (fetchGeneration !== galleryFetchGenerationRef.current) {
      return;
    }

    if (response) {
      const dataFormatted = formatGalleryContentEntriesFromResponse(response);
      const groupedData = groupGalleryCarousels(dataFormatted);

      if (shouldMergeAlertPlaceholders) {
        const currentNotifications = notificationsRef.current;
        setData(mergePlaceholdersWithData(groupedData, currentNotifications));
      } else {
        setData(groupedData);
      }
    }

    if (fetchGeneration === galleryFetchGenerationRef.current) {
      setIsLoadingSource(false);
    }
  };

  const refreshGalleryAfterImport = async () => {
    const willReloadFromFilterEffect =
      filters.contentType !== ALL_FILTER || filters.dimensions !== ALL_FILTER;

    if (willReloadFromFilterEffect || filters.dateRange !== ALL_FILTER) {
      setFilters((prev) => ({
        ...prev,
        contentType: ALL_FILTER,
        dimensions: ALL_FILTER,
        dateRange: ALL_FILTER,
      }));
    }

    if (!willReloadFromFilterEffect) {
      await getInitialData();
    }
  };
  getInitialDataRef.current = refreshGalleryAfterImport;

  const getNextMediaImmediate = useCallback(async () => {
    if (isLoadingSource || loadLastItem || isFetchingNewContents || isCalling) {
      return;
    }

    const queryContext = getGalleryQueryContext();
    if (
      !isSameGalleryQueryContext(queryContext, galleryQueryContextRef.current)
    ) {
      return;
    }

    const fetchGeneration = galleryFetchGenerationRef.current;
    setIsFetchingNewContents(true);

    const guardedOnChangeLastKey = (key) => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onChangeLastKey(key);
      }
    };

    const guardedOnLoadLastItem = () => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onLoadLastItem();
      }
    };

    if (selectedSource === GALLERY_SOURCES.myContent) {
      const response = await dispatch(
        startGetGalleryContent({
          lastKey,
          onChangeLastKey: guardedOnChangeLastKey,
          limit: rowsPerPage,
          onLoadLastItem: guardedOnLoadLastItem,
          filter: queryContext.filter,
          filterType: queryContext.filterType,
          folderID: queryContext.folderID,
        })
      );

      if (
        fetchGeneration !== galleryFetchGenerationRef.current ||
        !isSameGalleryQueryContext(queryContext, galleryQueryContextRef.current)
      ) {
        setIsFetchingNewContents(false);
        return;
      }

      if (response) {
        const dataFormatted = formatGalleryContentEntriesFromResponse(response);
        const groupedData = groupGalleryCarousels(dataFormatted);
        setData((prevState) => {
          const existingIds = new Set(prevState.map((i) => i.id));
          const newItems = groupedData.filter((i) => !existingIds.has(i.id));
          if (newItems.length === 0) return prevState;
          // Only the new page needs formatting; re-grouping prevState would clone
          // every loaded item and force all of them to re-render.
          return [...prevState, ...newItems].sort(
            (a, b) => (b.creationTime || 0) - (a.creationTime || 0)
          );
        });
      }
    }

    setIsFetchingNewContents(false);
  }, [
    dispatch,
    getGalleryQueryContext,
    isCalling,
    isFetchingNewContents,
    isLoadingSource,
    lastKey,
    loadLastItem,
    onChangeLastKey,
    onLoadLastItem,
    rowsPerPage,
    selectedSource,
  ]);

  const scheduleGetNextMedia = useCallback(() => {
    onCancelGetNextMedia();
    getNextMediaTimerRef.current = setTimeout(() => {
      getNextMediaTimerRef.current = null;
      getNextMediaImmediate();
    }, 350);
  }, [getNextMediaImmediate, onCancelGetNextMedia]);

  // Reload data when a server-side gallery filter changes.
  useEffect(() => {
    if (modalOpen) {
      onCancelGetNextMedia();
      getInitialData();
    }
    //eslint-disable-next-line
  }, [
    filters.approvalStatus,
    filters.contentType,
    filters.dimensions,
    filters.folderID,
  ]);

  // Efecto original para carga inicial
  useEffect(() => {
    if (modalOpen) {
      getInitialData();
    }
    //eslint-disable-next-line
  }, [modalOpen]);

  // Optimización: Usar useRef para evitar llamadas múltiples durante scroll rápido
  const lastCallTimeRef = useRef(0);
  const THROTTLE_DELAY = 300; // 300ms entre llamadas para scroll más fluido

  useEffect(() => {
    if (
      !isNearScreen ||
      loadLastItem ||
      isFetchingNewContents ||
      isLoadingSource
    ) {
      return;
    }

    const now = Date.now();
    const timeSinceLastCall = now - lastCallTimeRef.current;

    if (timeSinceLastCall < THROTTLE_DELAY) {
      const remainingDelay = THROTTLE_DELAY - timeSinceLastCall;
      const timeoutId = setTimeout(() => {
        lastCallTimeRef.current = Date.now();
        scheduleGetNextMedia();
      }, remainingDelay);

      return () => clearTimeout(timeoutId);
    }

    lastCallTimeRef.current = now;
    scheduleGetNextMedia();
    //eslint-disable-next-line
  }, [isNearScreen, loadLastItem, isFetchingNewContents, isLoadingSource]);

  const dataFiltered = filteredData;
  const selectedGalleryItems = useMemo(
    () => getGalleryItemsBySelectionIds(data, selectedItems),
    [data, selectedItems]
  );
  const selectedContentIDs = useMemo(
    () => selectedGalleryItems.map((item) => item?.id).filter(Boolean),
    [selectedGalleryItems]
  );

  const disabledMoveFolderID = useMemo(
    () => getCommonFolderID({ items: selectedGalleryItems }),
    [selectedGalleryItems]
  );

  const currentFolderLabelForMove = useMemo(() => {
    if (activeFolderID) {
      return getFolderLabel({ folderID: activeFolderID });
    }
    return t(GALLERY_FOLDERS_ALL);
  }, [activeFolderID, getFolderLabel, t]);

  const onCallbackMoveToFolder = useCallback(
    async ({ folderID }) =>
      moveContents({
        contentIDs: selectedContentIDs,
        folderID,
      }),
    [moveContents, selectedContentIDs]
  );
  const onFailedLoadUrl = useCallback((index) => {
    setData((prevState) => {
      const newData = [...prevState];
      const filteredData = newData.filter((item, i) => i !== index);
      return filteredData;
    });
  }, []);

  const onDeleteItem = useCallback((index) => {
    setData((prevState) => {
      const deletedItem = prevState[index];
      const deletedSelectionId = getGalleryItemSelectionId(deletedItem);

      if (deletedSelectionId) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter(
            (selectionId) => selectionId !== deletedSelectionId
          )
        );
      }

      return prevState.filter((_, itemIndex) => itemIndex !== index);
    });
  }, []);

  const onDeleteSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    const result = await ConfirmAlert({
      title: t(DELETE_MULTIPLE_CONTENT_CONFIRMATION_TITLE),
      text: t(DELETE_MULTIPLE_CONTENT_CONFIRMATION_DESCRIPTION, {
        count: selectedItems.length,
      }),
      confirmButtonText: t(BUTTON_DELETE_CONTENT),
    });

    if (!result.isConfirmed) return;

    try {
      const validItems = getGalleryItemsBySelectionIds(
        data,
        selectedItems
      ).filter((item) => item?.id);

      if (validItems.length === 0) return;

      const deletePromises = validItems.map((item) =>
        dispatch(startDeleteContentFromListAndStorage({ contentID: item.id }))
      );

      await Promise.all(deletePromises);

      const deletedSelectionIds = new Set(
        validItems
          .map((item) => getGalleryItemSelectionId(item))
          .filter(Boolean)
      );

      setData((prevState) =>
        prevState.filter(
          (item) => !deletedSelectionIds.has(getGalleryItemSelectionId(item))
        )
      );

      setSelectedItems([]);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onDeleteSelectedItems",
          capture: "exception",
        })
      );
      console.error("Error deleting selected contents:", error);
    }
  };

  const onRemoveItemFromFolder = useCallback(
    (item) => {
      if (!item?.id) return;
      moveContents({ contentIDs: [item.id], folderID: null });
    },
    [moveContents]
  );

  const {
    label: deleteSelectedLabel,
    Icon: DeleteSelectedIcon,
    onAction: onDeleteOrRemoveSelectedItems,
  } = useFolderAwareDeleteAction({
    activeFolderID,
    deleteLabel: t(BUTTON_DELETE_CONTENT),
    deleteIcon: DeleteIcon,
    onDelete: onDeleteSelectedItems,
    onRemoveFromFolder: () =>
      moveContents({ contentIDs: selectedContentIDs, folderID: null }),
  });

  useEffect(() => {
    if (!modalOpen || !shouldMergeAlertPlaceholders) return;

    const loadingAlerts = notifications.filter(
      (alert) =>
        alert?.status === ALERTS_ASYNC_STATUS.LOADING &&
        alert?.type === "generatingCreative" &&
        Boolean(alert?.jobType)
    );
    const currentLoadingCount = loadingAlerts.length;
    const prevLoadingCount = prevLoadingCountRef.current;

    prevLoadingCountRef.current = currentLoadingCount;

    if (prevLoadingCount > currentLoadingCount) {
      getInitialData();
    } else {
      setData((prev) => mergePlaceholdersWithData(prev, notifications));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getInitialData is stable enough; avoids refetch loops
  }, [
    notifications,
    modalOpen,
    shouldMergeAlertPlaceholders,
    enableCreativeJobAlertPlaceholders,
    mergePlaceholdersWithData,
  ]);

  const onDownloadSelectedItems = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const selected = getGalleryItemsBySelectionIds(data, selectedItems);
    if (selected.length === 0) return;

    setIsDownloading(true);
    try {
      const singleUrls = [];
      let carouselCount = 0;

      for (const item of selected) {
        if (isCarouselItem(item)) {
          carouselCount += 1;
          const urls = getCarouselUrls(item);
          const fileName =
            carouselCount === 1
              ? "carousel.zip"
              : `carousel-${carouselCount}.zip`;
          await dispatch(startDownloadCarouselZip({ urls, fileName }));
        } else if (item.url) {
          singleUrls.push(item.url);
        }
      }

      if (singleUrls.length > 0) {
        if (
          singleUrls.length > GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD
        ) {
          await dispatch(
            startDownloadUrlsAsZip({
              urls: singleUrls,
              fileName: "creatives.zip",
            })
          );
        } else {
          await dispatch(startGetAwsDownloadUrl(singleUrls));
        }
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const onSelectItem = useCallback(
    (index) => {
      const item = dataFiltered[index];
      const selectionId = getGalleryItemSelectionId(item);
      if (!selectionId) return;

      const isLimitReached =
        multiple &&
        maxSelections &&
        !selectedItems.includes(selectionId) &&
        selectedItems.length >= maxSelections;
      if (isLimitReached) {
        SimpleAlert({
          title: t(GALLERY_MAX_SELECTIONS_REACHED, { limit: maxSelections }),
          icon: ALERT_ICON_TYPE_WARNING,
        });
        return;
      }

      setSelectedItems((prev) => {
        const isCurrentlySelected = prev.includes(selectionId);

        if (isCurrentlySelected) {
          return prev.filter((id) => id !== selectionId);
        }

        if (multiple) {
          if (maxSelections && prev.length >= maxSelections) {
            return prev;
          }
          return [...prev, selectionId];
        }

        return [selectionId];
      });
    },
    [multiple, maxSelections, dataFiltered, selectedItems, t]
  );

  const onSelectPreview = useCallback((indexOrData) => {
    // Puede recibir un número (índice) o un objeto con información del carrusel
    if (typeof indexOrData === "object" && indexOrData !== null) {
      setSelectPreview(indexOrData);
    } else {
      setSelectPreview(indexOrData);
    }
  }, []);

  const onSelectCarousel = useCallback(
    async (index) => {
      const item = dataFiltered[index];

      if (!item?.isCarousel || !item?.carouselImages) {
        return;
      }

      const carouselItems = item.carouselImages.map((img, idx) => ({
        url: img.url,
        from: item.from,
        id: img.id || `${item.id}-${idx}`,
        userID: item.userID,
        isCarousel: true,
        carouselImages: item.carouselImages,
        mediaData: img.mediaData || {},
      }));

      if (pathStorage) {
        const urls = carouselItems.map((item) => item.url);
        setIsUploading(true);
        const response = await dispatch(
          startSaveMediaInStorage({
            urls,
            path: pathStorage,
          })
        );

        if (!response.ok) {
          setIsUploading(false);
          return;
        }

        const urlsStored = response.data.urls;
        const contentsToSave = [];
        urlsStored.forEach((urlStored, idx) => {
          carouselItems[idx].url = urlStored;
          contentsToSave.push(
            stampFolderContent({ url: urlStored, type: "upload" })
          );
        });
        await dispatch(startAddContentsToList(contentsToSave));
        setIsUploading(false);
      }

      onCallbackSelectedItems(carouselItems);

      if (!closeActionsDisabled) {
        onCloseModal();
      }
    },
    [
      dataFiltered,
      pathStorage,
      dispatch,
      onCallbackSelectedItems,
      closeActionsDisabled,
      onCloseModal,
      stampFolderContent,
    ]
  );

  const onSendSelectedItems = useCallback(
    async (selectionIds) => {
      const idsToSend = selectionIds || selectedItems;
      if (!idsToSend || idsToSend.length === 0) return true;

      const dataSelectedItems = getGalleryItemsBySelectionIds(data, idsToSend)
        .map((item) => {
          if (!item) return null;

          const itemData = {
            url: item.url,
            from: item.from,
            id: item.id,
            userID: item.userID,
            isCarousel: item.isCarousel,
            carouselImages: item.carouselImages || null,
          };

          if (selectedSource === GALLERY_SOURCES.socialPosts) {
            if (item.postID) itemData.postID = item.postID;
            if (item.pageID) itemData.pageID = item.pageID;
            if (item.instagramID) itemData.instagramID = item.instagramID;
            if (item.integrationID) itemData.integrationID = item.integrationID;
            if (item.platform) itemData.platform = item.platform;
            if (item.mediaType) itemData.mediaType = item.mediaType;
            if (item.caption) itemData.caption = item.caption;
            if (item.permalink) itemData.permalink = item.permalink;
            if (item.timestamp) itemData.timestamp = item.timestamp;

            Object.keys(item).forEach((key) => {
              if (!Object.prototype.hasOwnProperty.call(itemData, key)) {
                itemData[key] = item[key];
              }
            });
          }

          return itemData;
        })
        .filter(Boolean);

      const needsStorageUpload =
        Boolean(pathStorage) &&
        (selectedSource === GALLERY_SOURCES.socialPosts ||
          selectedSource === GALLERY_SOURCES.adAccount);

      if (needsStorageUpload) {
        const urls = dataSelectedItems.map((item) => item.url).filter(Boolean);
        if (urls.length > 0) {
          setIsUploading(true);
          const response = await dispatch(
            startSaveMediaInStorage({
              urls,
              path: pathStorage,
            })
          );

          if (!response.ok) {
            setIsUploading(false);
            return false;
          }

          const urlsStored = response.data.urls;
          const contentsToSave = [];
          urlsStored.forEach((urlStored, index) => {
            if (!urlStored) return;
            if (dataSelectedItems[index]) {
              dataSelectedItems[index].url = urlStored;
            }
            contentsToSave.push(
              stampFolderContent({ url: urlStored, type: "upload" })
            );
          });
          await dispatch(startAddContentsToList(contentsToSave));
          setIsUploading(false);
        }
      }

      if (onValidateSelectedItems) {
        const isValid = await onValidateSelectedItems(dataSelectedItems);
        if (!isValid) {
          return false;
        }
      }

      onCallbackSelectedItems(dataSelectedItems);
      if (!closeActionsDisabled) {
        onCloseModal();
      }
      return true;
    },
    [
      closeActionsDisabled,
      data,
      dispatch,
      onCallbackSelectedItems,
      onCloseModal,
      onValidateSelectedItems,
      pathStorage,
      selectedItems,
      selectedSource,
      stampFolderContent,
    ]
  );

  const onSelectItemPicker = useCallback(
    async (index) => {
      if (isUploading) return;
      const result = await ConfirmAlert({
        title: t(INFORMATION),
        text: t(SELECT_CONTENT_CONFIRMATION_TEXT),
      });
      if (!result?.isConfirmed) return;

      const selectionId = getGalleryItemSelectionId(dataFiltered[index]);
      if (!selectionId) return;

      await onSendSelectedItems([selectionId]);
    },
    // eslint-disable-next-line
    [isUploading, onSendSelectedItems]
  );

  const onSourceSelection = async (source) => {
    setSelectedSource(source);
    setSelectedItems([]);
    setLoadLastItem(false);

    if (
      source === GALLERY_SOURCES.adAccount ||
      source === GALLERY_SOURCES.socialPosts ||
      source === GALLERY_SOURCES.canva
    ) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
          showPaywall: true,
        })
      );
      if (isBlocked) return;
    }

    if (source === GALLERY_SOURCES.adAccount) {
      if (!hasAdAccountIntegration) {
        console.warn("No ad account integration found");
        return;
      }
      setIsOpenAdAccountModal(true);
      return;
    } else if (source === GALLERY_SOURCES.socialPosts) {
      if (!hasSocialPostsIntegration) {
        console.warn("No social posts integration found");
        return;
      }
      if (
        hasMetaIntegration &&
        !hasLinkedInIntegration &&
        !finalMetaIntegrationID
      ) {
        console.warn("Meta integration ID missing");
        return;
      }
      setIsOpenSocialPostsModal(true);
      return;
    } else if (source === GALLERY_SOURCES.canva) {
      setIsOpenCanvaGalleryModal(true);
      return;
    } else if (source === GALLERY_SOURCES.myContent) {
      await getInitialData();
    }
  };

  const onOpenCreateWithCanva = useCallback(() => {
    document.getElementById(canvaCreateTriggerId)?.click();
  }, [canvaCreateTriggerId]);

  const onImportFromCanvaGallery = useCallback(() => {
    document.getElementById(canvaGalleryTriggerId)?.click();
  }, [canvaGalleryTriggerId]);

  const onOpenGoogleDriveImport = useCallback(() => {
    document.getElementById(googleDriveImportTriggerId)?.click();
  }, [googleDriveImportTriggerId]);

  const onCallbackSaveImageEdit = async (savePayload) => {
    const editedUrl =
      typeof savePayload === "string" ? savePayload : savePayload?.url;
    const mode =
      typeof savePayload === "string"
        ? "replace"
        : savePayload?.mode || "replace";
    const imageEditIndex = imageEditData?.index;

    if (!editedUrl) return;

    if (reviewEditCallbackRef.current) {
      reviewEditCallbackRef.current(editedUrl);
      reviewEditCallbackRef.current = null;
      setIsOpenImageEditModal(false);
      setImageEditData(null);
      return;
    }

    if (mode === "new") {
      const content = {
        url: editedUrl,
        from: "ai",
        userID: null,
      };

      const responseSavedImage = await dispatch(
        startAddContentsToList([stampFolderContent(content)])
      );
      if (responseSavedImage) {
        const newContentFormatted = Object.values(responseSavedImage)[0];
        setData((prev) => [newContentFormatted, ...prev]);
        notifyGalleryContentSaved({ contentIds: [newContentFormatted.id] });
      }
      return;
    }

    const isCarouselEdit =
      imageEditData?.isCarousel && imageEditData?.carouselIndex !== undefined;
    const carouselIndex = imageEditData?.carouselIndex;
    const itemId = imageEditData?.itemId;

    if (imageEditIndex !== undefined && imageEditIndex !== null) {
      const existingItem = data[imageEditIndex];
      if (existingItem) {
        const timestamps = getGalleryBumpTimestamps();

        if (isCarouselEdit && itemId) {
          setData((prev) =>
            applyGalleryReorderPreservingPlaceholders(prev, (rest) => {
              const carouselItem = rest.find((entry) => entry.id === itemId);
              if (!carouselItem?.carouselImages) {
                return rest;
              }

              const updatedCarouselImages = [...carouselItem.carouselImages];
              if (updatedCarouselImages[carouselIndex]) {
                updatedCarouselImages[carouselIndex] = {
                  ...updatedCarouselImages[carouselIndex],
                  url: editedUrl,
                };
              }

              return bumpGalleryItemToFront(rest, itemId, {
                carouselImages: updatedCarouselImages,
                url: updatedCarouselImages[0]?.url || carouselItem.url,
                ...timestamps,
              });
            })
          );

          dispatch(
            startUpdateCarouselImageUrl({
              contentID: itemId,
              carouselIndex: carouselIndex,
              url: editedUrl,
            })
          ).catch((err) => {
            dispatch(
              LaunchError(err, false, [], {
                functionName: "ModalGallery",
                capture: "exception",
              })
            );
            console.error("Error optimistically updating carousel:", err);
          });
          dispatch(startBumpContentInList({ id: itemId })).catch((err) => {
            dispatch(
              LaunchError(err, false, [], {
                functionName: "ModalGallery",
                capture: "exception",
              })
            );
            console.error("Error bumping carousel content:", err);
          });
          notifyGalleryContentSaved({ contentIds: [itemId] });
        } else {
          setData((prev) =>
            applyGalleryReorderPreservingPlaceholders(prev, (rest) =>
              bumpGalleryItemToFront(rest, existingItem.id, {
                url: editedUrl,
                ...timestamps,
              })
            )
          );

          dispatch(
            startBumpAndUpdateContentInList({
              url: editedUrl,
              id: existingItem.id,
            })
          ).catch((err) => {
            dispatch(
              LaunchError(err, false, [], {
                functionName: "ModalGallery",
                capture: "exception",
              })
            );
            console.error("Error optimistically updating content:", err);
          });
          notifyGalleryContentSaved({ contentIds: [existingItem.id] });
        }
      }
    }
  };

  const onCallbackSaveCanva = async (editedUrl) => {
    const canvaEditingIndex = canvaImageData?.index;
    const isCarouselEdit =
      canvaImageData?.isCarousel && canvaImageData?.carouselIndex !== undefined;
    const carouselIndex = canvaImageData?.carouselIndex;
    const itemId = canvaImageData?.itemId;

    if (canvaEditingIndex !== undefined && canvaEditingIndex !== null) {
      const existingItem = data[canvaEditingIndex];
      const timestamps = getGalleryBumpTimestamps();

      if (isCarouselEdit && itemId) {
        setData((prev) =>
          applyGalleryReorderPreservingPlaceholders(prev, (rest) => {
            const carouselItem = rest.find((entry) => entry.id === itemId);
            if (!carouselItem?.carouselImages) {
              return rest;
            }

            const updatedCarouselImages = [...carouselItem.carouselImages];
            if (updatedCarouselImages[carouselIndex]) {
              updatedCarouselImages[carouselIndex] = {
                ...updatedCarouselImages[carouselIndex],
                url: editedUrl,
              };
            }

            return bumpGalleryItemToFront(rest, itemId, {
              carouselImages: updatedCarouselImages,
              url: updatedCarouselImages[0]?.url || carouselItem.url,
              ...timestamps,
            });
          })
        );

        dispatch(
          startUpdateCarouselImageUrl({
            contentID: itemId,
            carouselIndex: carouselIndex,
            url: editedUrl,
          })
        ).catch((err) => {
          dispatch(
            LaunchError(err, false, [], {
              functionName: "onCallbackSaveCanva",
              capture: "exception",
            })
          );
          console.error("Error optimistically updating carousel (Canva):", err);
        });
        dispatch(startBumpContentInList({ id: itemId })).catch((err) => {
          dispatch(
            LaunchError(err, false, [], {
              functionName: "onCallbackSaveCanva",
              capture: "exception",
            })
          );
          console.error("Error bumping carousel content (Canva):", err);
        });
        notifyGalleryContentSaved({ contentIds: [itemId] });
      } else if (existingItem?.id) {
        setData((prev) =>
          applyGalleryReorderPreservingPlaceholders(prev, (rest) =>
            bumpGalleryItemToFront(rest, existingItem.id, {
              url: editedUrl,
              ...timestamps,
            })
          )
        );

        dispatch(
          startBumpAndUpdateContentInList({
            url: editedUrl,
            id: existingItem.id,
          })
        ).catch((err) => {
          dispatch(
            LaunchError(err, false, [], {
              functionName: "onCallbackSaveCanva",
              capture: "exception",
            })
          );
          console.error("Error optimistically updating content (Canva):", err);
        });
        notifyGalleryContentSaved({ contentIds: [existingItem.id] });
      }
    }

    if (canvaEditingIndex === undefined || canvaEditingIndex === null) {
      const content = {
        url: editedUrl,
        from: "canva",
        userID: null,
      };

      const responseSavedImage = await dispatch(
        startAddContentsToList([stampFolderContent(content)])
      );

      if (responseSavedImage) {
        const newContentFormatted = Object.values(responseSavedImage)[0];
        setData((prev) => {
          return [newContentFormatted, ...prev];
        });
        notifyGalleryContentSaved({ contentIds: [newContentFormatted.id] });
      }
    }

    setCanvaImageData(null);
    setIsOpenCanva(false);
  };

  useEffect(() => {
    if (forceSocialPostsSource) {
      onSourceSelection(GALLERY_SOURCES.socialPosts);
    }
    getInitialData();

    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => {
      if (contentApprovalListener) {
        dispatch(stopListenerContentApproval(contentApprovalListener));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedContentApprovalBlocked = useMemo(() => {
    if (selectedItems.length === 0) {
      return false;
    }

    return selectedItems.some((selectionId) => {
      const item = findGalleryItemBySelectionId(data, selectionId);
      return Boolean(item?.contentApprovalID || item?.approvalStatus);
    });
  }, [selectedItems, data]);

  const batchEditEligibleItems = useMemo(
    () => getBatchEditEligibleItems(data, selectedItems),
    [data, selectedItems]
  );

  const canOpenBatchImageEdit =
    galleryPermissions.canEditGallery && batchEditEligibleItems.length >= 1;

  const batchResizeEligibleItems = useMemo(
    () => getBatchResizeEligibleItems(data, selectedItems),
    [data, selectedItems]
  );
  const canOpenBatchResize =
    galleryPermissions.canEditGallery && batchResizeEligibleItems.length >= 1;

  const carouselCreationEligibleItems = useMemo(
    () => getCarouselCreationEligibleItems(data, selectedItems),
    [data, selectedItems]
  );
  const canCreateCarousel =
    galleryPermissions.canEditGallery &&
    carouselCreationEligibleItems.length > 0;

  const getContentApprovalDataForItem = useCallback(
    (item) => buildContentApprovalDataFromItem(item),
    []
  );

  const buildApprovalContentStates = useCallback(
    ({ approvalData = {}, contentIDs = [], fallbackStatus = null } = {}) => {
      if (Object.keys(approvalData?.contentStates || {}).length > 0) {
        return approvalData.contentStates;
      }

      if (Object.keys(approvalData?.contents || {}).length > 0) {
        return Object.entries(approvalData.contents).reduce(
          (contentStates, [contentID, entry]) => {
            if (!contentID) {
              return contentStates;
            }

            return {
              ...contentStates,
              [String(contentID)]: {
                status:
                  entry?.states?.status ||
                  fallbackStatus ||
                  approvalData?.status ||
                  null,
                currentDraftID:
                  entry?.states?.currentDraftID ||
                  approvalData?.currentDraftID ||
                  null,
                draftRevision:
                  Number(
                    entry?.states?.draftRevision ?? approvalData?.draftRevision
                  ) || 0,
              },
            };
          },
          {}
        );
      }

      return (contentIDs || []).reduce((contentStates, contentID) => {
        if (!contentID) {
          return contentStates;
        }

        return {
          ...contentStates,
          [String(contentID)]: {
            status: fallbackStatus || approvalData?.status || null,
            currentDraftID: approvalData?.currentDraftID || null,
            draftRevision: Number(approvalData?.draftRevision) || 0,
          },
        };
      }, {});
    },
    []
  );

  const getContentReviewModeForItem = useCallback(
    (item, approvalData = null) => {
      const contentState = getContentApprovalStateForContent({
        approvalData,
        contentID: item?.id,
        fallbackStatus: item?.approvalStatus || null,
        fallbackCurrentDraftID: item?.currentDraftID || null,
        fallbackDraftRevision: item?.draftRevision || 0,
      });

      return contentState.status === "approved" ? "approvedOnly" : "reviewable";
    },
    []
  );

  const getContentReviewContentIDs = useCallback(
    ({ approvalID, item, approvalData = null, reviewMode = "reviewable" }) => {
      const openedContentID = String(item?.id || "");
      if (!openedContentID) {
        return [];
      }

      if (reviewMode === "approvedOnly") {
        return [openedContentID];
      }

      const approvalContentIDs = getContentApprovalContentIDs(approvalData)
        .length
        ? getContentApprovalContentIDs(approvalData)
        : dataFiltered
            .filter((entry) => entry.contentApprovalID === approvalID)
            .map((entry) => String(entry?.id || ""))
            .filter(Boolean);

      if (approvalContentIDs.length === 0) {
        return [openedContentID];
      }

      const reviewableContentIDs = approvalContentIDs.filter((contentID) => {
        const matchingItem = dataFiltered.find(
          (entry) => String(entry?.id || "") === contentID
        );
        const contentState = getContentApprovalStateForContent({
          approvalData,
          contentID,
          fallbackStatus: approvalData
            ? null
            : matchingItem?.approvalStatus || matchingItem?.status || null,
          fallbackCurrentDraftID: approvalData
            ? null
            : matchingItem?.currentDraftID || null,
          fallbackDraftRevision: approvalData
            ? 0
            : matchingItem?.draftRevision || 0,
        });

        return isReviewableContentApprovalStatus(contentState.status);
      });

      return reviewableContentIDs.length > 0
        ? reviewableContentIDs
        : [openedContentID];
    },
    [dataFiltered]
  );

  const buildContentReviewBatchItems = useCallback(
    (contentIDs, rawItems) => {
      const grouped = groupGalleryCarousels(rawItems);
      const groupedById = new Map(
        grouped.map((entry) => [String(entry.id), entry])
      );

      return contentIDs
        .map((contentID) => groupedById.get(String(contentID)))
        .filter(Boolean);
    },
    // groupGalleryCarousels is stable for the modal session (no external deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const loadContentReviewBatchItems = useCallback(
    async (contentIDs, approvalData = null) => {
      if (!Array.isArray(contentIDs) || contentIDs.length === 0) {
        setContentReviewBatchItems([]);
        return;
      }

      const normalizedIDs = contentIDs.map(String);
      const loadedById = new Map(
        dataFiltered.map((entry) => [String(entry.id), entry])
      );
      const missingIDs = normalizedIDs.filter(
        (contentID) => !loadedById.has(contentID)
      );

      const mapWithApprovalData = (items) =>
        items.map((entry) =>
          mergeGalleryItemWithApprovalData(entry, approvalData)
        );

      if (missingIDs.length === 0) {
        const rawItems = normalizedIDs
          .map((contentID) => loadedById.get(contentID))
          .filter(Boolean);
        setContentReviewBatchItems(
          mapWithApprovalData(
            buildContentReviewBatchItems(normalizedIDs, rawItems)
          )
        );
        return;
      }

      let fetchedById = new Map();
      if (missingIDs.length > 0) {
        const response = await dispatch(
          startGetContentItemsByIds({ contentIDs: missingIDs })
        );

        if (response?.ok && Array.isArray(response.items)) {
          fetchedById = new Map(
            response.items.map((entry) => [String(entry.id), entry])
          );
        }
      }

      const rawItems = normalizedIDs
        .map(
          (contentID) => loadedById.get(contentID) || fetchedById.get(contentID)
        )
        .filter(Boolean);

      setContentReviewBatchItems(
        mapWithApprovalData(
          buildContentReviewBatchItems(normalizedIDs, rawItems)
        )
      );
    },
    [buildContentReviewBatchItems, dataFiltered, dispatch]
  );

  const syncBatchApprovalStatusInData = useCallback(
    (approvalID, contentStates = {}) => {
      if (!approvalID) {
        return;
      }

      setData((prev) =>
        prev.map((entry) => {
          const contentState = contentStates?.[String(entry?.id || "")];
          if (!contentState) {
            return entry;
          }

          return {
            ...entry,
            approvalStatus: contentState.status || null,
            currentDraftID: contentState.currentDraftID || null,
            draftRevision: Number(contentState.draftRevision) || 0,
            contentApprovalID: approvalID,
          };
        })
      );
    },
    []
  );

  const onEnableBulkContentApproval = useCallback(async () => {
    if (enablingContentApprovalId || selectedItems.length === 0) {
      return;
    }

    const contentIDs = getGalleryItemsBySelectionIds(data, selectedItems)
      .map((item) => item?.id)
      .filter(Boolean);

    if (contentIDs.length === 0 || selectedContentApprovalBlocked) {
      return;
    }

    const result = await ConfirmAlert({
      title: t(CONTENT_APPROVAL_CONFIRM_ENABLE_TITLE),
      text: t(CONTENT_APPROVAL_CONFIRM_ENABLE_DESCRIPTION),
      confirmButtonText: t(BUTTON_SEND),
      cancelButtonText: t(BUTTON_CANCEL),
    });

    if (!result.isConfirmed) {
      return;
    }

    setEnablingContentApprovalId("bulk");
    const response = await dispatch(startEnableContentApproval({ contentIDs }));
    setEnablingContentApprovalId(null);

    if (!response?.ok) {
      return;
    }

    const approvalID = response.approvalID;
    const contentStates = buildApprovalContentStates({
      approvalData: response,
      contentIDs,
      fallbackStatus: response.status || "pending",
    });

    syncBatchApprovalStatusInData(approvalID, contentStates);

    setApprovalSuccessDialog({
      open: true,
      approvalUrl: response.url,
      approvalID,
      reviewerID: response.reviewerID || "",
    });
    setSelectedItems([]);
  }, [
    data,
    dispatch,
    enablingContentApprovalId,
    selectedContentApprovalBlocked,
    selectedItems,
    syncBatchApprovalStatusInData,
    t,
    buildApprovalContentStates,
  ]);

  const onCopyContentApprovalLink = useCallback(() => {
    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  }, [t]);

  const onCloseContentReview = useCallback(async () => {
    setContentReviewState({
      open: false,
      index: null,
      approvalID: null,
      reviewContentIDs: [],
      openedContentID: null,
      reviewMode: "reviewable",
    });
    setReviewApprovalData(null);
    setIsLoadingReviewApproval(false);
    setContentReviewBatchItems([]);
    if (contentApprovalListener) {
      await dispatch(stopListenerContentApproval(contentApprovalListener));
      setContentApprovalListener(null);
    }
  }, [contentApprovalListener, dispatch]);

  const onOpenContentApprovalReview = useCallback(
    async (index, item) => {
      const approvalID = item?.contentApprovalID;
      if (!item?.id || !approvalID) {
        return;
      }

      if (contentApprovalListener) {
        await dispatch(stopListenerContentApproval(contentApprovalListener));
      }

      setReviewApprovalData(null);
      setIsLoadingReviewApproval(true);
      setContentReviewBatchItems([]);

      setContentReviewState({
        open: true,
        index,
        approvalID,
        reviewContentIDs: [String(item.id)],
        openedContentID: item.id,
        reviewMode: "reviewable",
      });

      dispatch(
        startListenerContentApproval({
          approvalID,
          onReferenceAvailable: (listener) =>
            setContentApprovalListener(listener),
          onUpdatedDataCallback: (approvalData) => {
            const contentStates = buildApprovalContentStates({
              approvalData,
              contentIDs:
                getContentApprovalContentIDs(approvalData).length > 0
                  ? getContentApprovalContentIDs(approvalData)
                  : [String(item.id)],
              fallbackStatus: approvalData.status || null,
            });
            const nextApprovalData = {
              ...approvalData,
              contentStates,
            };
            const reviewMode = getContentReviewModeForItem(
              item,
              nextApprovalData
            );
            const nextReviewContentIDs = getContentReviewContentIDs({
              approvalID,
              item,
              approvalData: nextApprovalData,
              reviewMode,
            });

            setReviewApprovalData(nextApprovalData);
            setIsLoadingReviewApproval(false);

            setContentReviewState((current) => {
              if (current?.approvalID !== approvalID) {
                return current;
              }

              return {
                ...current,
                reviewContentIDs: nextReviewContentIDs,
                reviewMode,
              };
            });

            setContentReviewBatchItems((prev) => {
              const prevContentIDs = prev.map((entry) => String(entry.id));
              const shouldReloadBatchItems =
                prev.length === 0 ||
                (nextReviewContentIDs.length > 0 &&
                  !haveSameContentIDs(nextReviewContentIDs, prevContentIDs));

              if (shouldReloadBatchItems) {
                void loadContentReviewBatchItems(
                  nextReviewContentIDs,
                  nextApprovalData
                );
                return prev;
              }

              return prev.map((entry) =>
                mergeGalleryItemWithApprovalData(entry, nextApprovalData)
              );
            });

            syncBatchApprovalStatusInData(approvalID, contentStates);
          },
        })
      );
    },
    [
      contentApprovalListener,
      dispatch,
      buildApprovalContentStates,
      getContentReviewContentIDs,
      getContentReviewModeForItem,
      loadContentReviewBatchItems,
      syncBatchApprovalStatusInData,
    ]
  );

  useEffect(() => {
    if (!modalOpen) {
      contentReviewFetchAttemptedRef.current = false;
      openedContentReviewIDRef.current = null;
      return;
    }

    if (!initialContentReviewID || isLoadingSource) {
      return;
    }

    const contentID = initialContentReviewID;
    const existingIndex = dataFiltered.findIndex(
      (entry) => entry.id === contentID
    );

    if (existingIndex >= 0) {
      if (openedContentReviewIDRef.current === contentID) {
        return;
      }

      openedContentReviewIDRef.current = contentID;
      contentReviewFetchAttemptedRef.current = false;
      onOpenContentApprovalReview(existingIndex, dataFiltered[existingIndex]);
      onInitialContentReviewConsumed();
      return;
    }

    if (data.some((entry) => entry.id === contentID)) {
      setFilters({
        contentType: ALL_FILTER,
        dimensions: ALL_FILTER,
        dateRange: ALL_FILTER,
        folderID: ALL_FILTER,
      });
      return;
    }

    if (contentReviewFetchAttemptedRef.current) {
      return;
    }

    contentReviewFetchAttemptedRef.current = true;

    let isCancelled = false;

    (async () => {
      const response = await dispatch(startGetContentItemById({ contentID }));

      if (isCancelled) {
        return;
      }

      if (!response?.ok || !response?.data) {
        onInitialContentReviewConsumed();
        return;
      }

      setFilters({
        contentType: ALL_FILTER,
        dimensions: ALL_FILTER,
        dateRange: ALL_FILTER,
        folderID: ALL_FILTER,
      });
      setData((prev) => {
        const withoutDuplicate = prev.filter((entry) => entry.id !== contentID);
        return groupGalleryCarousels([response.data, ...withoutDuplicate]);
      });
    })();

    return () => {
      isCancelled = true;
    };
  }, [
    modalOpen,
    initialContentReviewID,
    isLoadingSource,
    dataFiltered,
    data,
    dispatch,
    onOpenContentApprovalReview,
    onInitialContentReviewConsumed,
  ]);

  const onSelectInitialPreview = useCallback(
    ({ index, item }) => {
      const isCarouselPreview =
        item?.isCarousel && item?.carouselImages?.length > 1;

      if (isCarouselPreview && initialPreviewCarouselIndex != null) {
        setSelectPreview({
          index,
          carouselIndex: initialPreviewCarouselIndex,
          isCarousel: true,
          isFromCarousel: true,
        });
        return;
      }

      setSelectPreview(index);
    },
    [initialPreviewCarouselIndex]
  );

  const onHiddenInitialPreview = useCallback(() => {
    setFilters({
      contentType: ALL_FILTER,
      dimensions: ALL_FILTER,
      dateRange: ALL_FILTER,
      folderID: ALL_FILTER,
    });
  }, []);

  useInitialPreviewSelection({
    isOpen: modalOpen,
    isReady: !isLoadingSource,
    previewId: initialGalleryEditAction ? null : initialPreviewItemID,
    items: dataFiltered,
    getId: getGalleryPreviewItemId,
    onSelect: onSelectInitialPreview,
    onConsume: onInitialPreviewItemConsumed,
    allItems: data,
    onHidden: onHiddenInitialPreview,
  });

  const initialGalleryEditConsumedRef = useRef(false);

  useEffect(() => {
    if (!modalOpen) {
      initialGalleryEditConsumedRef.current = false;
    }
  }, [modalOpen]);

  useEffect(() => {
    if (
      !modalOpen ||
      isLoadingSource ||
      !initialGalleryEditAction ||
      !initialPreviewItemID ||
      initialGalleryEditConsumedRef.current
    ) {
      return;
    }

    const previewId = String(initialPreviewItemID);
    const itemIndex = dataFiltered.findIndex(
      (entry) => String(getGalleryPreviewItemId(entry) ?? "") === previewId
    );

    if (itemIndex < 0) {
      const isHiddenInData = data.some(
        (entry) => String(getGalleryPreviewItemId(entry) ?? "") === previewId
      );

      if (isHiddenInData) {
        onHiddenInitialPreview();
      }

      return;
    }

    const item = dataFiltered[itemIndex];
    const carouselIndex = initialPreviewCarouselIndex ?? 0;
    const carouselImage = item?.carouselImages?.[carouselIndex];
    const currentUrl = item?.isCarousel ? carouselImage?.url : item?.url;
    const dimensions =
      item?.width && item?.height
        ? { width: item.width, height: item.height }
        : undefined;

    initialGalleryEditConsumedRef.current = true;
    onInitialGalleryEditConsumed();

    switch (initialGalleryEditAction) {
      case HOME_GALLERY_EDIT_ACTION.CANVA:
        openCanvaWithImage({
          url: currentUrl,
          dimensions,
          index: itemIndex,
          ...(item?.isCarousel && {
            carouselIndex,
            isCarousel: true,
            itemId: item.id,
          }),
        });
        break;
      case HOME_GALLERY_EDIT_ACTION.IA:
        openImageEditWithIA({
          url: currentUrl,
          dimensions,
          index: itemIndex,
          itemId: item.id,
          ...(item?.isCarousel && {
            carouselIndex,
            isCarousel: true,
          }),
        });
        break;
      case HOME_GALLERY_EDIT_ACTION.ANIMATE:
        onAnimateImageFromModal({ index: itemIndex });
        break;
      case HOME_GALLERY_EDIT_ACTION.RESIZE:
        onResizeFromModal({
          index: itemIndex,
          format: initialGalleryEditResize?.format,
          formats: initialGalleryEditResize?.formats,
          width: initialGalleryEditResize?.width,
          height: initialGalleryEditResize?.height,
        });
        break;
      default: {
        const exhaustiveCheck = initialGalleryEditAction;
        void exhaustiveCheck;
        break;
      }
    }
  }, [
    data,
    dataFiltered,
    initialGalleryEditAction,
    initialGalleryEditResize,
    initialPreviewCarouselIndex,
    initialPreviewItemID,
    isLoadingSource,
    modalOpen,
    onAnimateImageFromModal,
    onHiddenInitialPreview,
    onInitialGalleryEditConsumed,
    onResizeFromModal,
    openCanvaWithImage,
    openImageEditWithIA,
  ]);

  const applyContentReviewMediaUpdate = (entry, change) => {
    const { contentID, url, urls, type } = change;

    if (entry.id !== contentID) {
      return entry;
    }

    if (urls?.length) {
      const carouselImages = urls.map((carouselUrl, carouselIdx) => ({
        url: carouselUrl,
        id: `${contentID}-${carouselIdx}`,
      }));
      return {
        ...entry,
        isCarousel: true,
        type: "carousel",
        urls,
        url: urls[0],
        carouselImages,
      };
    }

    const extension = getExtensionFile(url || "");
    const mediaType = VIDEO_FORMATS.includes(extension) ? "video" : "image";

    return {
      ...entry,
      url,
      isCarousel: false,
      carouselImages: null,
      type: type || mediaType || entry.type,
    };
  };

  const applyContentReviewBumpState = useCallback(
    (contentID, bumpResult, approvalID) => {
      const normalizedContentID = String(contentID || "");
      if (!normalizedContentID || !bumpResult?.ok) {
        return;
      }

      const nextState = {
        status: bumpResult.status || "pending",
        currentDraftID: bumpResult.currentDraftID || null,
        draftRevision: Number(bumpResult.draftRevision) || 0,
      };

      setReviewApprovalData((prev) => {
        if (!prev) {
          return prev;
        }

        const nextContentStates = {
          ...(prev.contentStates || {}),
          [normalizedContentID]: nextState,
        };
        const existingContents = prev.contents?.[normalizedContentID];
        const nextContents = existingContents
          ? {
              ...(prev.contents || {}),
              [normalizedContentID]: {
                ...existingContents,
                states: nextState,
              },
            }
          : prev.contents;

        return {
          ...prev,
          contentStates: nextContentStates,
          ...(nextContents ? { contents: nextContents } : {}),
          comments: (prev.comments || []).map((comment) =>
            String(comment?.contentID) === normalizedContentID
              ? { ...comment, isStale: true }
              : comment
          ),
        };
      });

      setContentReviewBatchItems((prev) =>
        prev.map((entry) =>
          String(entry?.id || "") === normalizedContentID
            ? {
                ...entry,
                approvalStatus: nextState.status,
                currentDraftID: nextState.currentDraftID,
                draftRevision: nextState.draftRevision,
              }
            : entry
        )
      );

      if (approvalID) {
        syncBatchApprovalStatusInData(approvalID, {
          [normalizedContentID]: nextState,
        });
      }
    },
    [syncBatchApprovalStatusInData]
  );

  const onSaveContentReview = useCallback(
    async (pendingChanges) => {
      const changes = Array.isArray(pendingChanges)
        ? pendingChanges
        : [pendingChanges];
      const validChanges = changes.filter(
        (change) => change?.contentID && (change.url || change.urls?.length)
      );

      if (validChanges.length === 0) {
        return { ok: false, savedContentIDs: [] };
      }

      setIsSavingContentReview(true);
      const savedContentIDs = [];

      for (const change of validChanges) {
        const { contentID, url, urls } = change;
        let saved = false;

        if (urls?.length) {
          saved = await dispatch(
            startReplaceContentMediaInList({
              contentID,
              urls,
              type: "carousel",
            })
          );
        } else if (url) {
          const extension = getExtensionFile(url);
          const mediaType = VIDEO_FORMATS.includes(extension)
            ? "video"
            : "image";
          saved = await dispatch(
            startReplaceContentMediaInList({
              contentID,
              url,
              type: mediaType,
            })
          );
        }

        if (saved) {
          savedContentIDs.push(contentID);
        }
      }

      if (savedContentIDs.length > 0) {
        const savedChanges = validChanges.filter((change) =>
          savedContentIDs.includes(change.contentID)
        );

        const applySavedChanges = (entry) => {
          const change = savedChanges.find(
            (savedChange) => savedChange.contentID === entry.id
          );
          return change ? applyContentReviewMediaUpdate(entry, change) : entry;
        };

        setData((prev) => prev.map(applySavedChanges));
        setContentReviewBatchItems((prev) => prev.map(applySavedChanges));

        let bumpedAny = false;
        for (const contentID of savedContentIDs) {
          const bumpResult = await dispatch(
            startBumpContentApprovalDraft({ contentID })
          );

          if (bumpResult?.ok) {
            applyContentReviewBumpState(
              contentID,
              bumpResult,
              contentReviewState.approvalID
            );
            bumpedAny = true;
          }
        }

        if (bumpedAny) {
          await SimpleAlert({
            title: t(READY),
            text: t(CONTENT_APPROVAL_SAVE_REVISION_SUCCESS_DESCRIPTION),
            icon: ALERT_ICON_TYPE_SUCCESS,
          });
        }
      }

      setIsSavingContentReview(false);

      return {
        ok: savedContentIDs.length > 0,
        savedContentIDs,
      };
    },
    [applyContentReviewBumpState, contentReviewState.approvalID, dispatch, t]
  );

  const onReplaceFromReview = useCallback(({ onSelected }) => {
    replacePickerCallbackRef.current = onSelected;
    setIsReplacePickerForReviewOpen(true);
  }, []);

  const onEditFromReview = useCallback(
    ({ contentID, url, carouselIndex, isCarousel, onSaved }) => {
      reviewEditCallbackRef.current = onSaved;
      setImageEditData({
        url,
        fromContentApprovalReview: true,
        itemId: contentID,
        ...(isCarousel
          ? {
              isCarousel: true,
              carouselIndex,
            }
          : {}),
      });
      setIsOpenImageEditModal(true);
    },
    []
  );

  const contentReviewActiveItem = useMemo(() => {
    if (!contentReviewState.openedContentID) {
      return dataFiltered[contentReviewState.index] || null;
    }

    return (
      contentReviewBatchItems.find(
        (entry) => entry?.id === contentReviewState.openedContentID
      ) ||
      data.find((entry) => entry?.id === contentReviewState.openedContentID) ||
      dataFiltered[contentReviewState.index] ||
      null
    );
  }, [
    contentReviewBatchItems,
    contentReviewState.index,
    contentReviewState.openedContentID,
    data,
    dataFiltered,
  ]);

  const showFooterAddSelectedButton =
    showFooterUseSelectedButton ||
    (!isOutsideCampaign && galleryPermissions.showFooterAddToCampaign) ||
    (isPicker && !closeActionsDisabled);
  const showFooterCreateCampaignButton =
    isOutsideCampaign &&
    !showFooterUseSelectedButton &&
    galleryPermissions.showFooterCreateCampaign;

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseGalleryModal}
        fullWidth={true}
        fullScreen
        scroll="paper"
        aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
        enableHistoryManagement={enableHistoryManagement}
        disableScrollLock={isOpenCalendarModal}
        sxContent={{
          px: 0,
          pb: 12,
          mt: 1,
          overflowX: "hidden",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          "&::-webkit-scrollbar": {
            width: "8px",
            display: "block",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.4)",
            },
          },
        }}
        refContent={galleryRef}
        zIndex={dialogZIndex}
        hasTitle={false}
      >
        <UploadFileButton
          id={uploadInputId}
          value=""
          onChange={onAddContent}
          acceptedFormats={acceptedFormats}
          multiple
          minDimensions={minDimensions}
          pathStorage={pathStorage}
          pathRTDB={pathRTDB}
          modeUpload={modeUpload}
          onCallbackLoading={(loading) => {
            setIsUploading(loading);
          }}
          isHidden={true}
        />
        <UploadFileButton
          id={folderUploadInputId}
          value=""
          directory
          onChange={onAddContent}
          onFileSelect={onDirectoryFileSelect}
          onUploadSettled={() => {
            pendingUploadFolderIDRef.current = null;
          }}
          acceptedFormats={acceptedFormats}
          multiple
          minDimensions={minDimensions}
          pathStorage={pathStorage}
          pathRTDB={pathRTDB}
          modeUpload={modeUpload}
          onCallbackLoading={(loading) => {
            setIsUploading(loading);
          }}
          isHidden={true}
        />

        <Box
          sx={{
            mb: 1,
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              pr: paddings.pr,
              pl: paddings.pl,
              justifyContent: "space-between",
              position: "absolute",
              top: 0,
              zIndex: 1,
              width: "100%",
              height: 84,
              backgroundColor: "background.paper",
            }}
          >
            <TitlePage>{t(GALLERY_SELECT_SOURCE)}</TitlePage>
            <IconButton onClick={onCloseGalleryModal} aria-label="Cerrar modal">
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              mt: 6,
              borderTop: `1px solid ${BORDER_GRAY_COLOR}`,
            }}
          >
            {galleryPermissions.canEditGallery && (
              <Box
                data-testid="add-content-buttons"
                sx={getGalleryToolbarRowSx()}
              >
                <Box
                  sx={{
                    display: { xs: "grid", md: "grid", lg: "flex" },
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr",
                      lg: "none",
                    },
                    gap: 1.5,
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <Button
                    id="button-create-with-ia-modal-gallery"
                    variant="contained"
                    color="primary"
                    startIcon={
                      <SparklesIcon
                        sx={{
                          color: "white !important",
                          fontSize: 20,
                        }}
                      />
                    }
                    onClick={async () => {
                      const isBlocked = await dispatch(
                        getIsBlockedActions({
                          action: ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
                          showPaywall: true,
                        })
                      );
                      if (isBlocked) return;

                      const createWithIAInitialValues = {
                        ...(initialValues || {}),
                      };
                      if (isOutsideCampaign) {
                        delete createWithIAInitialValues.language;
                      }
                      const createWithIAId = openCreateWithIA({
                        initialValues: createWithIAInitialValues,
                        onClosePreviousComponents: onCloseModal,
                        contentFolderID: activeFolderID,
                        onCloseModal: async () => {
                          createWithIAIdRef.current = null;
                          await getInitialDataRef.current?.();
                        },
                      });
                      createWithIAIdRef.current = createWithIAId;
                    }}
                    sx={{ width: { xs: "100%", md: "100%", lg: "auto" } }}
                  >
                    {t(BUTTON_CREATE_FROM_SCRATCH)}
                  </Button>
                  <NewFeatureTooltip featureId="gallery-templates">
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<LayoutTemplate size={20} />}
                      onClick={() => openTemplatesList()}
                      sx={{ width: { xs: "100%", md: "100%", lg: "auto" } }}
                    >
                      {t(TEMPLATES_TITLE)}
                    </Button>
                  </NewFeatureTooltip>

                  {showUploadDeviceOption && !forceSocialPostsSource && (
                    <GalleryUploadMenu
                      isUploading={isUploading}
                      onUploadFiles={() => onOpenDeviceUpload()}
                      onUploadFolder={() =>
                        onOpenDeviceUpload({ directory: true })
                      }
                      sx={{ width: { xs: "100%", md: "100%", lg: "auto" } }}
                    />
                  )}

                  {!forceSocialPostsSource && (
                    <ImportDropdownButton
                      showGoogleDriveOption={showGoogleDriveOption}
                      showAdAccountOption={showAdAccountOption}
                      showSocialPostsOption={showSocialPostsOption}
                      hasAdAccountIntegration={hasAdAccountIntegration}
                      hasSocialPostsIntegration={hasSocialPostsIntegration}
                      isLoadingGoogleDrivePicker={isLoadingGoogleDrivePicker}
                      isUploading={isUploading}
                      isLoadingSource={isLoadingSource}
                      onCreateWithCanva={onOpenCreateWithCanva}
                      onImportFromGoogleDrive={onOpenGoogleDriveImport}
                      onImportFromAdAccount={() =>
                        onSourceSelection(GALLERY_SOURCES.adAccount)
                      }
                      onImportFromSocialPosts={() =>
                        onSourceSelection(GALLERY_SOURCES.socialPosts)
                      }
                      onImportFromCanvaGallery={onImportFromCanvaGallery}
                      sx={{
                        width: { xs: "100%", md: "100%", lg: "auto" },
                      }}
                    />
                  )}

                  {showSocialPostsOption &&
                    hasSocialPostsIntegration &&
                    forceSocialPostsSource && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ShareIcon />}
                        onClick={() => onSourceSelection("socialPosts")}
                        disabled={isLoadingSource}
                        sx={{
                          width: { xs: "100%", md: "100%", lg: "auto" },
                          backgroundColor: "primary.main",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        }}
                      >
                        {t(GALLERY_SOURCE_SOCIAL_POSTS)}
                      </Button>
                    )}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                position: "sticky",
                top: 50,
                zIndex: 4,
                bgcolor: "background.paper",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              }}
            >
              <Box
                sx={getGalleryToolbarRowSx({
                  backgroundColor: "#f0f0f0",
                })}
              >
                <Box
                  sx={{
                    display: { xs: "grid", md: "grid", lg: "flex" },
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.5,
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <FilterSelect
                    value={filters.contentType}
                    onChange={(e) =>
                      onFilterChange("contentType", e.target.value)
                    }
                    options={translatedContentTypeOptions}
                    sx={{
                      ...filterSelectSx,
                      width: { xs: "100%", lg: "auto" },
                    }}
                    icon={<FileText size={16} color="black" />}
                  />

                  <FilterSelect
                    value={filters.dimensions}
                    onChange={(e) =>
                      onFilterChange("dimensions", e.target.value)
                    }
                    options={translatedDimensionsOptions}
                    sx={{
                      ...filterSelectSx,
                      width: { xs: "100%", lg: "auto" },
                    }}
                    icon={<Maximize2 size={16} color="black" />}
                  />

                  {galleryPermissions.showContentApproval ? (
                    <FilterSelect
                      value={filters.approvalStatus}
                      onChange={(e) =>
                        onFilterChange("approvalStatus", e.target.value)
                      }
                      options={translatedApprovalStatusOptions}
                      sx={{
                        ...filterSelectSx,
                        width: { xs: "100%", lg: "auto" },
                      }}
                      icon={<BadgeCheck size={16} color="black" />}
                    />
                  ) : null}

                  <Button
                    onClick={resetFilters}
                    color="secondary"
                    sx={{
                      height: 42,
                      width: { xs: "100%", lg: "auto" },
                    }}
                  >
                    {t(FILTERS_GALLERY_RESET_FILTERS)}
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  ...getGalleryToolbarRowSx(),
                  alignItems: "flex-start",
                  pt: 2,
                  // Con scroll horizontal el pb queda en 1: la barra ocupa ese hueco.
                  // Sin scroll se iguala al pt.
                  pb: folderBarHasHorizontalScroll ? 1 : 2,
                }}
              >
                <GalleryFolderBar
                  folders={folders}
                  activeFolderID={activeFolderID}
                  selectedContentIDs={selectedContentIDs}
                  canEdit={galleryPermissions.canEditGallery}
                  isLoading={
                    isLoadingFolders || isCreatingFolder || isMovingContents
                  }
                  onSelectFolder={onSelectFolder}
                  onOpenCreateFolder={openCreateFolderModal}
                  onDropMany={onDropMany}
                  onRenameFolder={openRenameFolderModal}
                  onDeleteFolder={onDeleteFolder}
                  onHorizontalOverflowChange={setFolderBarHasHorizontalScroll}
                />
              </Box>
            </Box>

            {!isUploading && isLoadingSource && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Loader
                  size={70}
                  hasMessage={true}
                  message={t(LOADING_CONTENT)}
                />
              </Box>
            )}

            {!isLoadingSource && (
              <>
                <Gallery
                  data={dataFiltered}
                  highlightedIds={highlightedContentIds}
                  sxGallery={{
                    pl: paddings.pl,
                    pr: paddings.pr,
                    pt: 2,
                  }}
                  onCloseModal={onCloseModal}
                  lastItemRef={fromRef}
                  loadLastItem={loadLastItem}
                  releaseMediaWhenHidden
                  virtualized
                  scrollElementRef={galleryRef}
                  onFailedLoadUrl={onFailedLoadUrl}
                  onSelectItem={isPicker ? onSelectItemPicker : onSelectItem}
                  selectedItems={selectedItemsSet}
                  selectionBy="id"
                  onSelectPreview={onSelectPreview}
                  onSelectCarousel={onSelectCarousel}
                  onDeleteItem={onDeleteItem}
                  onRemoveItemFromFolder={
                    activeFolderID ? onRemoveItemFromFolder : null
                  }
                  multiple={isPicker ? true : multiple}
                  showSourceSelector={false}
                  showFiltersSelector={false}
                  showResizeFormatButton={
                    galleryPermissions.showResizeFormatButton
                  }
                  showDeleteButton={
                    galleryPermissions.showDeleteButton &&
                    (isPicker ? false : true)
                  }
                  overlayPickerMode={isPicker}
                  showActionButtons={galleryPermissions.showActionButtons}
                  showPreviewExpandOnly={
                    galleryPermissions.showPreviewExpandOnly
                  }
                  showPostButton={galleryPermissions.showPostButton}
                  onCallbackResizeFormat={onResizeFromModal}
                  onAnimateImage={onAnimateImageFromModal}
                  from={undefined}
                  acceptedFormats={acceptedFormats}
                  minDimensions={minDimensions}
                  onEditWithCanva={openCanvaWithImage}
                  onEditWithIA={openImageEditWithIA}
                  onEditWithVideo={openVideoEditorFromItem}
                  itemDimensions={itemDimensions}
                  showRating={showRatings}
                  scheduledDate={dateToUse}
                  fromCalendar={fromCalendar}
                  selectedItemsArray={selectedItems}
                  maxSelections={isPicker ? 1 : maxSelections}
                  draggableForFolders={canEditGalleryFolders}
                  selectedItemsCount={selectedItems.length}
                  onRefreshCalendar={onRefreshCalendarFromGallery}
                  onScheduledShareSuccess={onScheduledShareSuccessFromGallery}
                  onShareSuccess={onShareSuccessFromGallery}
                  showContentApproval={galleryPermissions.showContentApproval}
                  getContentApprovalData={getContentApprovalDataForItem}
                  enablingContentApprovalId={enablingContentApprovalId}
                  showContentApprovalEnableButton={false}
                  onOpenContentApprovalReview={onOpenContentApprovalReview}
                />
                <Box
                  sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1300,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    py: 1.5,
                    pl: paddings.pl,
                    pr: paddings.pr,
                    backgroundColor: "#fff",
                    borderTop: `1px solid ${BORDER_GRAY_COLOR}`,
                    [modalGalleryFooterStackMq]: {
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                      [modalGalleryFooterStackMq]: {
                        width: "100%",
                        justifyContent: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(GALLERY_VIEW_SCORES)}
                      </Typography>
                      <Switch
                        id="toggle-gallery-ratings"
                        isActive={showRatings}
                        onChangeIsActive={setShowRatings}
                        hasLabel={false}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      color="secondary"
                      id="button-open-calendar-modal-gallery"
                      startIcon={<CalendarMonth />}
                      onClick={() => setIsOpenCalendarModal(true)}
                    >
                      {t(VIEW_CALENDAR)}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 1,
                      flex: { xs: "1 1 100%", sm: "0 1 auto" },
                      minWidth: 0,
                      [modalGalleryFooterStackMq]: {
                        width: "100%",
                        flex: "none",
                        justifyContent: "center",
                      },
                    }}
                  >
                    <GallerySelectedCountChip
                      selectedCount={selectedItems.length}
                    />
                    <Button
                      id="modal-gallery-download-selected"
                      color="secondary"
                      iconOnly
                      ariaLabel={t(BUTTON_DOWNLOAD)}
                      tooltip={t(BUTTON_DOWNLOAD)}
                      disabled={selectedItems.length === 0}
                      loading={isDownloading}
                      startIcon={<DownloadIcon sx={{ fontSize: 24 }} />}
                      onClick={onDownloadSelectedItems}
                    />
                    {galleryPermissions.canEditGallery ? (
                      <Button
                        id="modal-gallery-move-to-folder-selected"
                        color="secondary"
                        iconOnly
                        ariaLabel={t(GALLERY_FOLDERS_MOVE)}
                        tooltip={t(GALLERY_FOLDERS_MOVE)}
                        disabled={
                          selectedItems.length === 0 || isMovingContents
                        }
                        startIcon={<DriveFileMoveIcon sx={{ fontSize: 24 }} />}
                        onClick={(event) => {
                          event?.preventDefault();
                          event?.stopPropagation();
                          openMoveFolderModal();
                        }}
                      />
                    ) : null}
                    {galleryPermissions.canEditGallery ? (
                      <MultiFormatResizeMenu
                        id="modal-gallery-resize-selected"
                        showAllFormats
                        variant="resizeOnly"
                        mode="footer"
                        TriggerIcon={PhotoSizeSelectLargeIcon}
                        disabled={
                          selectedItems.length === 0 || !canOpenBatchResize
                        }
                        onResizeFormat={onBatchResizeFormat}
                      />
                    ) : null}
                    {galleryPermissions.canEditGallery ? (
                      <Button
                        id="modal-gallery-batch-edit-selected"
                        color="secondary"
                        iconOnly
                        ariaLabel={t(MODAL_BATCH_IMAGE_EDIT_FOOTER_ACTION)}
                        tooltip={t(MODAL_BATCH_IMAGE_EDIT_FOOTER_ACTION)}
                        disabled={
                          selectedItems.length === 0 || !canOpenBatchImageEdit
                        }
                        startIcon={<EditIcon sx={{ fontSize: 24 }} />}
                        onClick={(event) => {
                          event?.preventDefault();
                          event?.stopPropagation();
                          onOpenBatchImageEditModal();
                        }}
                      />
                    ) : null}
                    {galleryPermissions.canEditGallery ? (
                      <Button
                        id="modal-gallery-create-carousel-selected"
                        color="secondary"
                        iconOnly
                        ariaLabel={t(GALLERY_CREATE_CAROUSEL)}
                        tooltip={t(GALLERY_CREATE_CAROUSEL)}
                        disabled={isCreatingCarousel || !canCreateCarousel}
                        loading={isCreatingCarousel}
                        startIcon={<ViewCarouselIcon sx={{ fontSize: 24 }} />}
                        onClick={(event) => {
                          event?.preventDefault();
                          event?.stopPropagation();
                          onCreateCarouselFromSelection();
                        }}
                      />
                    ) : null}
                    {galleryPermissions.showFooterDeleteSelected && (
                      <Button
                        id="modal-gallery-delete-selected"
                        color="secondary"
                        iconOnly
                        ariaLabel={deleteSelectedLabel}
                        tooltip={deleteSelectedLabel}
                        disabled={selectedItems.length === 0}
                        startIcon={<DeleteSelectedIcon sx={{ fontSize: 24 }} />}
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          onDeleteOrRemoveSelectedItems();
                        }}
                      />
                    )}
                    {galleryPermissions.showContentApproval && (
                      <Button
                        id="modal-gallery-send-content-approval-selected"
                        color="secondary"
                        iconOnly
                        ariaLabel={t(CAMPAIGN_APPROVAL_SEND_FOR_REVIEW)}
                        tooltip={t(CAMPAIGN_APPROVAL_SEND_FOR_REVIEW)}
                        disabled={
                          selectedItems.length === 0 ||
                          selectedContentApprovalBlocked ||
                          Boolean(enablingContentApprovalId)
                        }
                        loading={enablingContentApprovalId === "bulk"}
                        startIcon={
                          <FactCheckOutlinedIcon sx={{ fontSize: 24 }} />
                        }
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          onEnableBulkContentApproval();
                        }}
                      />
                    )}
                    {showFooterAddSelectedButton && (
                      <Button
                        color="primary"
                        disabled={selectedItems.length === 0}
                        onClick={(e) => {
                          e?.preventDefault();
                          e?.stopPropagation();
                          onSendSelectedItems();
                        }}
                      >
                        {t(BUTTON_ADD_SELECTED_CONTENT)}
                      </Button>
                    )}
                    {showFooterCreateCampaignButton && (
                      <Button
                        color="primary"
                        onClick={onHandleCreateCampaign}
                        disabled={selectedItems.length === 0}
                      >
                        {t(BUTTON_CREATE_CAMPAIGN2)}
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {isOpenSocialPostsModal && (
            <ModalGalleryAdsPlatforms
              modalOpen={isOpenSocialPostsModal}
              onCloseModal={() => {
                if (forceSocialPostsSource) {
                  onCloseModal();
                  return;
                }
                setIsOpenSocialPostsModal(false);
              }}
              onCallbackSelectedItems={async (dataSelectedItems) => {
                if (forceSocialPostsSource) {
                  onCallbackSelectedItems(dataSelectedItems);
                  onCloseModal();
                  return;
                }
                setIsOpenSocialPostsModal(false);
                setSelectedSource(GALLERY_SOURCES.myContent);
                setData([]);
                await refreshGalleryAfterImport();
              }}
              pathStorage={pathStorage}
              contentFolderID={activeFolderID}
              multiple={true}
              compact={true}
              forceSocialPostsSource={
                forceSocialPostsSource && !isForceLinkedInPostsSource
              }
              linkedInIntegrationID={finalLinkedInIntegrationID}
              linkedInAdAccountID={finalLinkedInAdAccountID}
              linkedInAuthorType={finalLinkedInAuthorType}
              forceLinkedInPostsSource={isForceLinkedInPostsSource}
              adFormat={initialValues?.adFormat}
              integrationID={finalMetaIntegrationID}
              adAccountID={finalMetaAdAccountID}
              pageID={finalMetaPageID}
            />
          )}
          {isOpenAdAccountModal && (
            <ModalGalleryAdAccount
              modalOpen={isOpenAdAccountModal}
              onCloseModal={() => setIsOpenAdAccountModal(false)}
              onCallbackSelectedItems={async () => {
                setIsOpenAdAccountModal(false);
                setSelectedSource(GALLERY_SOURCES.myContent);
                setData([]);
                await refreshGalleryAfterImport();
              }}
              pathStorage={pathStorage}
              contentFolderID={activeFolderID}
              multiple={true}
              compact={true}
            />
          )}
          <ButtonCanvaAction
            id={canvaCreateTriggerId}
            onClick={async () => {
              const isBlocked = await dispatch(
                getIsBlockedActions({
                  action: ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
                  showPaywall: true,
                })
              );
              if (isBlocked) return;

              setIsOpenCanva(true);
            }}
            sx={{ display: "none" }}
          />
          <ButtonCanvaAction
            id={canvaGalleryTriggerId}
            onClick={() => onSourceSelection(GALLERY_SOURCES.canva)}
            sx={{ display: "none" }}
          />
          <ButtonGoogleDriveAction
            id={googleDriveImportTriggerId}
            onClick={async () => {
              const isBlocked = await dispatch(
                getIsBlockedActions({
                  action: ADS_CAMPAIGN_ADD_CONTENT_GALLERY_ACTION,
                  showPaywall: true,
                })
              );
              if (isBlocked) return;

              setIsOpenGoogleDrivePickerAccountModal(true);
            }}
            sx={{ display: "none" }}
          />
          {isOpenGoogleDrivePickerAccountModal && (
            <ModalSelectGoogleDriveAccount
              modalOpen={isOpenGoogleDrivePickerAccountModal}
              onCloseModal={() => setIsOpenGoogleDrivePickerAccountModal(false)}
              onConfirm={async ({ integrationID }) => {
                setIsOpenGoogleDrivePickerAccountModal(false);
                await dispatch(
                  startSetLastSelectedIntegrationDataByPlatform({
                    platform: GOOGLE_DRIVE,
                    integrationID,
                  })
                );
                await onImportFromGoogleDrive({ integrationID });
              }}
            />
          )}
          {isOpenCanvaGalleryModal && (
            <ModalGalleryCanva
              modalOpen={isOpenCanvaGalleryModal}
              onCloseModal={() => setIsOpenCanvaGalleryModal(false)}
              onCallbackSelectedItems={async () => {
                setIsOpenCanvaGalleryModal(false);
                setSelectedSource(GALLERY_SOURCES.myContent);
                setData([]);
                await refreshGalleryAfterImport();
              }}
              pathStorage={pathStorage}
              contentFolderID={activeFolderID}
              multiple={true}
              compact={true}
            />
          )}
          {!isLoadingSource && dataFiltered.length === 0 && loadLastItem && (
            <Box
              sx={{
                minHeight: "400px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 0,
                position: "relative",
                zIndex: 0,
                pr: paddings.pr,
                pl: paddings.pl,
              }}
            >
              <Typography variant="h4" align="center">
                {t(MODAL_GALLERY_EMPTY_CONTENT)}
              </Typography>
            </Box>
          )}

          {selectPreview !== null &&
            (() => {
              // Manejar cuando selectPreview es un objeto con información del carrusel
              const previewIndex =
                typeof selectPreview === "object" && selectPreview !== null
                  ? selectPreview.index
                  : selectPreview;
              const previewItem = dataFiltered[previewIndex];
              const previewSelectionId = getGalleryItemSelectionId(previewItem);
              const isCarouselPreview =
                typeof selectPreview === "object" &&
                selectPreview !== null &&
                selectPreview.isCarousel;
              const isFromCarousel =
                typeof selectPreview === "object" &&
                selectPreview !== null &&
                selectPreview.isFromCarousel;
              const initialCarouselIndex =
                isCarouselPreview && selectPreview.carouselIndex !== undefined
                  ? selectPreview.carouselIndex
                  : 0;

              // Si es carrusel, mantener todas las imágenes pero establecer el índice inicial
              const itemToShow =
                isCarouselPreview &&
                previewItem?.isCarousel &&
                previewItem?.carouselImages
                  ? {
                      ...previewItem,
                      _initialCarouselIndex: initialCarouselIndex,
                    }
                  : previewItem;

              return (
                <ModalMedia
                  modalOpen={selectPreview !== null}
                  onCloseModal={() => {
                    setSelectPreview(null);
                  }}
                  isPicker={isPicker}
                  url={itemToShow?.url}
                  item={itemToShow}
                  isFromCarousel={isFromCarousel}
                  onShareSuccess={onShareSuccessFromGallery}
                  onDeleteSuccess={() => onDeleteItem(previewIndex)}
                  onEditWithCanva={(imageData) =>
                    openCanvaWithImage({
                      ...imageData,
                      index: previewIndex,
                      ...(isFromCarousel && {
                        carouselIndex:
                          imageData.carouselIndex ??
                          selectPreview.carouselIndex,
                        isCarousel: true,
                        itemId: imageData.itemId || previewItem?.id,
                      }),
                    })
                  }
                  onEditWithIA={(imageData) =>
                    openImageEditWithIA({
                      ...imageData,
                      index: previewIndex,
                      itemId: imageData.itemId || previewItem?.id,
                      ...(isFromCarousel && {
                        carouselIndex:
                          imageData.carouselIndex ??
                          selectPreview.carouselIndex,
                        isCarousel: true,
                      }),
                    })
                  }
                  onResizeFormat={({
                    format,
                    formats,
                    width,
                    height,
                    onCreditsConfirmed,
                  }) =>
                    onResizeFromModal({
                      index: previewIndex,
                      format,
                      formats,
                      width,
                      height,
                      onCreditsConfirmed,
                    })
                  }
                  onAnimateImage={() =>
                    onAnimateImageFromModal({ index: previewIndex })
                  }
                  showEditButton={galleryPermissions.showEditInPreview}
                  showShareButton={
                    !fromCalendar && galleryPermissions.showShareInPreview
                  }
                  showDeleteButton={galleryPermissions.showDeleteInPreview}
                  contentApprovalData={getContentApprovalDataForItem(
                    previewItem
                  )}
                  onOpenContentApprovalReview={() =>
                    onOpenContentApprovalReview(previewIndex, previewItem)
                  }
                  customActions={
                    galleryPermissions.showPreviewCustomActions ? (
                      <>
                        {!isUploading && !closeActionsDisabled && (
                          <IconButton
                            sx={{
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              color: "white",
                              width: 40,
                              height: 40,
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.8)",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: 20,
                              },
                            }}
                            onClick={async (e) => {
                              e?.preventDefault();
                              e?.stopPropagation();
                              if (!previewSelectionId) return;

                              if (selectedItems.includes(previewSelectionId)) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (selectionId) =>
                                      selectionId !== previewSelectionId
                                  )
                                );
                              } else {
                                if (multiple) {
                                  onSelectItem(previewIndex);
                                } else {
                                  const response = await onSendSelectedItems([
                                    previewSelectionId,
                                  ]);
                                  if (!response) {
                                    return;
                                  }
                                }
                              }
                              setSelectPreview(null);
                            }}
                            disabled={isUploading}
                            type="icon"
                            loaderColor="white"
                          >
                            {previewSelectionId &&
                            selectedItems.includes(previewSelectionId) ? (
                              <CloseIcon
                                sx={{ color: "white", fill: "white" }}
                              />
                            ) : (
                              <DoneIcon
                                sx={{ color: "white", fill: "white" }}
                              />
                            )}
                          </IconButton>
                        )}
                      </>
                    ) : null
                  }
                />
              );
            })()}
          {isOpenCanva && (
            <ModalCanva
              pathStorage={pathStorage}
              modalOpen={isOpenCanva}
              onCloseModal={onCloseCanvaModal}
              onCallbackSave={async (url) => {
                await onCallbackSaveCanva(url);
                return;
              }}
              imageUrl={canvaImageData?.url}
              imageDimensions={canvaImageData?.dimensions}
            />
          )}
          {isOpenImageEditModal && (
            <ModalImageEdit
              isOpen={isOpenImageEditModal}
              onClose={onCloseImageEditModal}
              initialImageUrl={imageEditData?.url}
              initialImageEditSession={null}
              contentID={imageEditData?.itemId}
              isCarousel={Boolean(imageEditData?.isCarousel)}
              carouselIndex={imageEditData?.carouselIndex}
              onCallbackSave={async (url) => {
                await onCallbackSaveImageEdit(url);
                return;
              }}
              showSaveAlert={!imageEditData?.fromContentApprovalReview}
              pathStorage={pathStorage}
            />
          )}
          {isOpenBatchImageEditModal && batchEditItems.length > 0 ? (
            <ModalBatchImageEdit
              isOpen={isOpenBatchImageEditModal}
              onClose={onCloseBatchImageEditModal}
              initialItems={batchEditItems}
              onCallbackApply={onApplyBatchImageEditResults}
            />
          ) : null}
          <ModalCreateContentFolder
            modalOpen={isOpenCreateFolderModal}
            folders={folders}
            parentFolderID={createFolderParentID}
            parentFolderLabel={createFolderParentLabel}
            isLoading={isCreatingFolder}
            onCloseModal={closeCreateFolderModal}
            onCallbackCreate={onCreateFolderInContext}
          />
          <ModalMoveContentsToFolder
            modalOpen={isOpenMoveFolderModal}
            folders={folders}
            currentFolderLabel={currentFolderLabelForMove}
            createParentFolderID={createFolderParentID}
            createParentFolderLabel={createFolderParentLabel}
            disabledFolderID={disabledMoveFolderID}
            isLoading={isMovingContents}
            isCreatingFolder={isCreatingFolder}
            onCloseModal={closeMoveFolderModal}
            onCallbackCreate={onCreateFolderInContext}
            onCallbackMove={onCallbackMoveToFolder}
          />
          <ModalRenameContentFolder
            modalOpen={isOpenRenameFolderModal}
            folders={folders}
            folder={renameFolderTarget}
            isLoading={isRenamingFolder}
            onCloseModal={closeRenameFolderModal}
            onCallbackRename={onRenameFolder}
          />
        </Box>
      </ModalBasicLayout>
      {isOpenCalendarModal && (
        <ModalCalendar
          modalOpen={isOpenCalendarModal}
          onCloseModal={onCloseCalendarModal}
          navigateToScheduledRequest={calendarNavigateRequest}
          onNavigateToScheduledConsumed={onNavigateToScheduledConsumed}
          onRegisterRefreshScheduledContents={onRegisterCalendarRefresh}
          onPublicationMarksChanged={refreshGalleryPublicationSources}
        />
      )}
      <ModalAnimateImagePreview {...previewModalProps} />
      <ApprovalEnableSuccessDialog
        modalOpen={approvalSuccessDialog.open}
        onCloseModal={() =>
          setApprovalSuccessDialog((prev) => ({ ...prev, open: false }))
        }
        approvalType="content"
        approvalUrl={approvalSuccessDialog.approvalUrl}
        approvalID={approvalSuccessDialog.approvalID}
        reviewerID={approvalSuccessDialog.reviewerID}
      />
      {contentReviewState.open &&
      contentReviewState.index != null &&
      contentReviewActiveItem ? (
        <ContentApprovalReviewModal
          modalOpen={contentReviewState.open}
          onCloseModal={onCloseContentReview}
          item={contentReviewActiveItem}
          batchItems={contentReviewBatchItems}
          approvalData={reviewApprovalData}
          isLoading={isLoadingReviewApproval}
          comments={reviewApprovalData?.comments || []}
          isSaving={isSavingContentReview}
          onCopyLink={onCopyContentApprovalLink}
          onReplace={onReplaceFromReview}
          onEdit={onEditFromReview}
          onSave={onSaveContentReview}
        />
      ) : null}
      {isReplacePickerForReviewOpen ? (
        <ModalGallery
          modalOpen={isReplacePickerForReviewOpen}
          multiple={false}
          dialogZIndex={1400}
          onCloseModal={() => {
            setIsReplacePickerForReviewOpen(false);
            replacePickerCallbackRef.current = null;
          }}
          onCallbackSelectedItems={(selectedItems) => {
            const picked = selectedItems?.[0];
            if (picked?.id) {
              const fullItem =
                data.find((entry) => entry.id === picked.id) || picked;
              replacePickerCallbackRef.current?.(fullItem);
            }
            replacePickerCallbackRef.current = null;
            setIsReplacePickerForReviewOpen(false);
          }}
          closeActionsDisabled
        />
      ) : null}
      {googleDriveLoadingPhase &&
        createPortal(
          <BackDropLoader
            open={!!googleDriveLoadingPhase}
            platform={GOOGLE_DRIVE}
            from={
              googleDriveLoadingPhase === "importing"
                ? MODE_BACK_DROP_CONNECT.GOOGLE_DRIVE_IMPORTING
                : MODE_BACK_DROP_CONNECT.GOOGLE_DRIVE_CONFIGURING
            }
          />,
          document.body
        )}
    </>
  );
};

export default ModalGallery;
